import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, RefreshCw, Loader2, Save, Lock, AlertTriangle, Info, Building2, ArrowUpRight,
  HardHat, MessageSquareText, X, Paperclip, ChevronRight, Reply, Check, Ban, Database, Unlink, ShoppingCart,
  Layers, MapPinned,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { TicketThread } from '@/components/tickets/TicketThread';
import { MentionTextarea, finalizeMentions, type MentionUser } from '@/components/tickets/MentionTextarea';
import { SnippetInsert } from '@/components/tickets/SnippetInsert';
import { cn, addressPoint } from '@/lib/utils';
import { StreetViewButton } from '@/components/custom/StreetView';
import { ApiError, apiFetch } from '@/lib/api-client';
import { reportTicketReopen, reopenFailed, reopenSentence } from '@/lib/reopenToast';
import { formatDeliveryStatus, isBlockedDeliveryCode } from '@/lib/deliveryStatus';
import { GlassCard } from '@/components/custom/GlassCard';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { TicketReclassifyMenu } from '@/components/tickets/TicketReclassifyMenu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  fetchAddressValidation,
  refreshAddressValidationReference,
  saveAddressValidation,
  escalateAddressValidation,
  returnEscalatedValidation,
  upsertSiblingMapping,
  sendToBuildAddressValidation,
  fetchValidationHistory,
  uploadTicketAttachment,
  askOperatorForTicket,
  ticketSendToBuild,
  ticketEscalate,
  ticketReturnEscalation,
  acquireValidationLock,
  releaseValidationLockOnLeave,
  forceReleaseValidationLock,
  useFacadeCocon,
  useSsvTsa,
  useBuildFiles,
  useOpenOrders,
  useSameAddressUnits,
  type ValidationLockState,
} from '@/hooks/useAddressValidation';
import { useAuth } from '@/hooks/useAuth';
import { fetchQueueForView, readQueueState, sortQueueRows } from '@/lib/queue-view';
import { AddressEnrichmentPanel } from '@/components/tickets/AddressEnrichmentPanel';
import { AddressReferenceView } from '@/components/tickets/AddressReferenceView';
import { AddressManagementMenu } from '@/components/tickets/AddressManagementMenu';
import { BuildRepliedMarker } from '@/components/tickets/BuildRepliedMarker';
import { UnlinkTicketModal } from '@/components/tickets/UnlinkTicketModal';
import { ReleaseAddressPanel } from '@/components/tickets/ReleaseAddressPanel';
import { TicketAttachments } from '@/components/tickets/TicketAttachments';
import {
  TicketAddressBar, TicketResolveCard, ticketAddressErrorText, ticketAddressLabel, isFinalAddressStatus,
} from '@/components/tickets/TicketAddresses';
import type {
  AddressValidationPanelsResponse,
  AddressValidationStatus,
  MduApartmentDto,
  AuditLogItem,
  UniticketWriteResult,
  UniticketReopenReport,
  EnrichmentSsvTsa,
} from '@uniticket/shared';
import {
  houseNumberExtensionCoherent, houseNumberDigitsOnly, computeAttentionPoints, capacityNeedsCocon, unitShowsOpenOrderCart,
  findDeclaredDuplicates, duplicatesOfInput,
} from '@uniticket/shared';
import { mduAddress, declaredAddress } from '@/lib/apartmentAddress';
import { AttentionBadge, AttentionDetails } from '@/components/tickets/AttentionPoints';
import { CapacityPill, CapacityDetailModal, capacityUnitsFromApartments } from '@/components/tickets/CapacityPanel';
import { SsvTsaPill } from '@/components/tickets/SsvTsaPill';
import { BuildFilePills, BuildFileUnitsDetail, type BuildFileUnitHit, type PillKind } from '@/components/tickets/BuildFilePills';
import { OpenOrderCart, OpenOrdersNotice, openOrdersFor } from '@/components/tickets/OpenOrderCart';
import { SameAddressRows, SameAddressCard, SameAddressNotice, SameAddressBanner } from '@/components/tickets/SameAddressUnits';
import { JvidLink } from '@/components/dmp/DmpLookup';
import { DetailModal, SsvTsaCard, addressLine } from '@/components/dmp/DmpConsolePanel';
import { useServiceFileFixes, NETWORK_ID_CHECKS } from '@/components/dmp/ServiceFileFixes';
import { PostalCodeLocalityHint } from '@/components/tickets/PostalCodeLocalityHint';

const EDIT_FIELDS = [
  { key: 'streetName', labelKey: 'fields.street' },
  { key: 'houseNumber', labelKey: 'fields.houseNumber' },
  { key: 'houseNumberExtension', labelKey: 'fields.houseNumberExt' },
  { key: 'busNumber', labelKey: 'fields.busNumber' },
  { key: 'postalCode', labelKey: 'fields.postalCode' },
  { key: 'municipality', labelKey: 'fields.municipality' },
  { key: 'city', labelKey: 'fields.city' },
  { key: 'dwellingUnitType', labelKey: 'fields.dwellingUnitType' },
] as const;
type EditKey = typeof EDIT_FIELDS[number]['key'];

interface FormState {
  status: AddressValidationStatus;
  remark: string;
  mapping: Record<EditKey, string>;
}

// La hauteur de départ des TROIS zones de message de la fenêtre — question à l'opérateur, message du verdict,
// question jointe à l'envoi chez Build (N43 ①, décision du user du 08-10 : 6 lignes, au lieu de 2 ou 3 ; un message
// à un opérateur tient rarement en trois lignes, et on ne relit pas ce qu'on ne voit pas).
const MESSAGE_ROWS = 6;

// Le nombre de colonnes du tableau des logements, en UN endroit : les sous-en-têtes d'adresse s'étendent sur toute
// la largeur (`colSpan`), et un nombre recopié à la main se décale à la première colonne ajoutée (N42 : la 8ᵉ).
const APARTMENT_TABLE_COLS = 8;

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// N45 (plan validé le 20-10-2026) — l'onglet ouvert vit dans l'URL (`?jvid=`) : un rechargement, un lien partagé
// rouvrent la même adresse. Absent = l'adresse principale, la seule qu'un ticket avait avant.
function parseJvidParam(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

function withJvid(params: URLSearchParams, jvid: number | null): URLSearchParams {
  const next = new URLSearchParams(params);
  if (jvid == null) next.delete('jvid');
  else next.set('jvid', String(jvid));
  return next;
}

/** Le `?jvid=` qui montre ces panneaux : aucun pour l'adresse principale. */
function jvidParamOf(p: AddressValidationPanelsResponse): number | null {
  const primary = p.addresses?.find((a) => a.primary)?.jvid ?? null;
  return p.multiAddress && p.activeJvid != null && p.activeJvid !== primary ? p.activeJvid : null;
}

/** Le ticket attend-il un envoi fait pour lui tout entier (Build, question à l'opérateur, escalade) ? Même lecture que
 *  le serveur (`ticketHandOffPending`) : pendant ce temps, aucune adresse d'un ticket à plusieurs adresses ne prend de
 *  verdict. Rend ce qu'il attend, ou null. */
function ticketWaitingFor(tk: AddressValidationPanelsResponse['ticket']): 'build' | 'operator' | 'escalation' | null {
  if (tk.buildHandedOffAt && !tk.buildReplyAt) return 'build';
  if (tk.questionAskedAt && !tk.operatorReplyAt) return 'operator';
  if (tk.escalatedAt && tk.escalatedToId && !tk.returnedAt) return 'escalation';
  return null;
}

// Group the building's apartments by DISTINCT street address (street + house no + ext, ignoring bus) —
// one belmap_building_id can legitimately span several addresses (e.g. two neighbouring entrances). The
// grouping makes that visible in the table (sub-headers + a banner) instead of one flat, misleading list.
// Encounter order is preserved (the backend already sorts by house/ext/bus).
// The ticket's own address as a synthetic apartment entry, so the per-apartment sheet (reference +
// Release/Un-release panel) can open for an SDU ticket too — an SDU has no "Other apartments" table,
// which used to leave its address with NO release entry point at all (user case: an operator asking to
// "delete" an address, which per the no-DELETE rule is handled by un-releasing it). Null when the
// panels carry no reference jvid.
function currentAsApartment(p: AddressValidationPanelsResponse): MduApartmentDto | null {
  if (p.reference?.jvid == null) return null;
  return {
    jvid: p.reference.jvid,
    streetName: p.reference.streetName ?? null,
    houseNumber: p.reference.houseNumber ?? null,
    houseNumberExtension: p.reference.houseNumberExtension ?? null,
    busNumber: p.reference.busNumber ?? null,
    isCurrent: true,
    pstFile: p.pstFile ?? null,
    attributes: p.referenceAttributes,
    reference: p.reference,
  };
}

function groupApartmentsByAddress(list: MduApartmentDto[]): { label: string; rows: MduApartmentDto[] }[] {
  const groups: { label: string; rows: MduApartmentDto[] }[] = [];
  const byLabel = new Map<string, { label: string; rows: MduApartmentDto[] }>();
  for (const a of list) {
    const label = mduAddress(a);
    let g = byLabel.get(label);
    if (!g) { g = { label, rows: [] }; byLabel.set(label, g); groups.push(g); }
    g.rows.push(a);
  }
  return groups;
}

// "Not in the deployment list" = we HAVE DMP attributes for this apartment but scopelistV15 (the
// deployment-list date) is empty → the line is NOT active and does not appear at the customer. Guard on
// attributes being present so a row whose DMP data failed to load isn't wrongly flagged as inactive.
function notInDeploymentList(a: MduApartmentDto): boolean {
  return !!a.attributes && !a.attributes.scopelistV15;
}

// Amber row highlight: not in the deployment list OR status NOT_AVAILABLE — both mean the line is
// not live at the customer.
function inactiveApartment(a: MduApartmentDto): boolean {
  return notInDeploymentList(a) || a.attributes?.status === 'NOT_AVAILABLE';
}

// Pre-fill priority for the Panel-2 mapping form, evaluated PER FIELD with blanks skipped: the saved
// address_mapping_table value wins; an empty/absent field falls back to the ticket's reference snapshot —
// i.e. the address the creator encoded on UniTicket (getValidationForTicket sets reference = the ticket
// snapshot at window-open). So a ticket with no saved mapping row opens pre-filled from the creator's
// submitted address instead of blank. Returns the first non-blank value, else '' (treated as "no value").
function prefill(...vals: Array<string | null | undefined>): string {
  for (const v of vals) {
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return '';
}

// Merge a freshly-fetched panels payload onto the current one WITHOUT swapping `panels.mapping` — it
// is the edit-diff baseline (form.mapping is diffed against it in handleSave), so replacing it would
// turn a concurrent writer's untouched-field change into a phantom edit that the next Save reverts.
// Advance only the live reference, MDU list, concurrency token and honesty banners. Shared by the
// "Refresh from DMP" button and the post-409 reconcile so the invariant lives in one place.
function mergeRefreshedPanels(
  prev: AddressValidationPanelsResponse,
  fresh: AddressValidationPanelsResponse,
): AddressValidationPanelsResponse {
  return {
    ...prev,
    reference: fresh.reference,
    referenceSource: fresh.referenceSource,
    // B-6 / N28 (2026-09-22) : « Actualiser » doit relire l'ÉTAT aussi, sinon le bandeau « archivée » et
    // le bloc de liaison resteraient ceux du chargement — une adresse réparée entre-temps continuerait
    // d'être annoncée morte. Les candidats suivent le même chemin : ils dépendent de cet état.
    referenceState: fresh.referenceState,
    addressCandidates: fresh.addressCandidates,
    referenceAttributes: fresh.referenceAttributes,
    mduApartments: fresh.mduApartments,
    expectedMappingUpdatedAt: fresh.expectedMappingUpdatedAt,
    isStale: fresh.isStale,
    triggeredByOtherTicket: fresh.triggeredByOtherTicket,
    escalationTarget: fresh.escalationTarget,
    returnTarget: fresh.returnTarget,
    buildTarget: fresh.buildTarget,
    // N45 — les onglets suivent : une adresse créée depuis le ticket s'y ajoute d'office. Le bloc ticket aussi : ses
    // envois (Build, question, escalade) décident si une adresse peut prendre un verdict. Ni l'un ni l'autre n'est une
    // base de comparaison pour le formulaire.
    ticket: fresh.ticket,
    activeJvid: fresh.activeJvid,
    addresses: fresh.addresses,
    multiAddress: fresh.multiAddress,
  };
}

export function AddressValidationPage() {
  const { t } = useTranslation(['tickets', 'common']);
  const { id } = useParams<{ id: string }>();
  // N45 — l'adresse du ticket ouverte (son onglet) ; null = la principale.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlJvid = parseJvidParam(searchParams.get('jvid'));
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  // Neighbours of the current ticket IN THE VALIDATOR'S VIEW — both ends, so the chain can be walked
  // backwards too (user ask 2026-08-06: re-reading the one just handled shouldn't mean going back to
  // the list). Both wrap around at their end of the list.
  const [nextTicketId, setNextTicketId] = useState<string | null>(null);
  const [prevTicketId, setPrevTicketId] = useState<string | null>(null);
  const [panels, setPanels] = useState<AddressValidationPanelsResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // N31 — la fenêtre « Délier ce JVID » (admins).
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [sendingToBuild, setSendingToBuild] = useState(false);
  const [pendingGuardOpen, setPendingGuardOpen] = useState(false);
  const [rejectGuardOpen, setRejectGuardOpen] = useState(false);
  // Optional reviewer file, pushed onto the Uniticket ticket right after a successful Save (≤ 10 MB).
  // Why the last save was refused — rendered as a PERSISTENT block right above the action buttons
  // (user ask 2026-08-04: « à proximité du bouton Enregistrer, avoir une erreur qui stipule quelle est
  // l'erreur »). A toast alone was not enough: it fades, and the reviewer was left staring at a form
  // that simply refused to save. Cleared as soon as anything in the form changes.
  const [saveBlocked, setSaveBlocked] = useState<string | null>(null);
  const [attachFile, setAttachFile] = useState<File | null>(null);
  // Bumped after a successful upload to remount <TicketAttachments> so the new file shows immediately.
  const [attachmentsVersion, setAttachmentsVersion] = useState(0);
  // Bumped after any action that POSTS on the Uniticket ticket → TicketThread refetches so the
  // just-posted message shows without an F5 (matters when the auto-advance has no next ticket).
  const [threadVersion, setThreadVersion] = useState(0);
  // Optional PUBLIC operator question typed inside the Send-to-Build confirm (user case 2026-08-03,
  // UT-2026-000907: Build opinion needed AND info missing from Proximus — one action does both).
  const [buildQuestion, setBuildQuestion] = useState('');
  // Which styled hand-off confirm is open (replaces the native window.confirm dialogs).
  const [confirmOpen, setConfirmOpen] = useState<null | 'escalate' | 'build' | 'return'>(null);
  const [returning, setReturning] = useState(false);
  // Panel 2 « Valider » (user ask 2026-08-06, F6): editing a text field — a postal-code correction, say —
  // wrote nothing until the whole ticket was validated, so the reviewer had no idea whether the change had
  // been recorded: « je l'ai noté sur le tableau de droite, mais y a pas de validation, y a rien ».
  const [mappingBaseline, setMappingBaseline] = useState<Record<EditKey, string> | null>(null);
  const [savingMapping, setSavingMapping] = useState(false);
  const [mappingSaved, setMappingSaved] = useState(false);
  // Anti-collision lock (user ask 2026-07-30): null until the first acquire answers. When someone
  // else holds it, ALL actions below are disabled + the red banner shows — reading stays free
  // (user decision). The server enforces with 423 on every mutating route regardless of this UI.
  const [lock, setLock] = useState<ValidationLockState | null>(null);
  const lockedByOther = lock != null && !lock.held;
  const [selectedSibling, setSelectedSibling] = useState<MduApartmentDto | null>(null);
  // Le détail de capacité de l'adresse dont on a cliqué la pastille. Même motif que `selectedSibling` :
  // l'état vit dans la PAGE et une SEULE modale est rendue à la racine, plutôt qu'une par ligne de
  // tableau. On garde le groupe entier, pas son libellé : la modale recalcule sur ses unités.
  const [capacityGroup, setCapacityGroup] = useState<{ label: string; rows: MduApartmentDto[] } | null>(null);
  // C0.1 (17-09) — COCON fait foi pour les adresses QD / QDX. La page le lit UNE fois, pour toutes ces adresses
  // du ticket, en tâche de fond (1,1 à 2,0 s mesurées en prod) ; pastilles, détail, « En savoir plus » et
  // formulaire d'ajout reçoivent cette MÊME lecture, sinon ils pourraient annoncer deux nombres. Déclaré ici,
  // avant les retours anticipés du rendu, parce que c'est un hook.
  const coconJvids = useMemo(() => {
    const apts = panels?.mduApartments;
    if (!apts?.length) return [];
    return groupApartmentsByAddress(apts)
      .filter((g) => capacityNeedsCocon(capacityUnitsFromApartments(g.rows)))
      .flatMap((g) => g.rows.filter((a) => !a.added).map((a) => a.jvid));
  }, [panels?.mduApartments]);
  const facadeCocon = useFacadeCocon(coconJvids);
  // SSV / TSA (17-09) — la pastille de la console, à côté de Capacité. Même principe que COCON : UNE lecture par
  // ticket, en tâche de fond, pour tous ses JVID — l'adresse du ticket comprise, qui porte la pastille d'un SDU.
  const ssvTsaJvids = useMemo(() => {
    const ids = (panels?.mduApartments ?? []).filter((a) => !a.added).map((a) => a.jvid);
    if (panels?.reference?.jvid != null) ids.push(panels.reference.jvid);
    return ids;
  }, [panels?.mduApartments, panels?.reference?.jvid]);
  const ssvTsa = useSsvTsa(ssvTsaJvids);
  // Service file / Deployment list (22-10) — les pastilles de la console, à côté de SSV / TSA, pour la même liste
  // de JVID et lues de la même façon.
  const buildFiles = useBuildFiles(ssvTsaJvids);
  const [buildFileOpen, setBuildFileOpen] = useState<{ kind: PillKind; label: string; hits: BuildFileUnitHit[] } | null>(null);
  // N42 (08-10) — les ordres en cours, pour le caddie de chaque logement : la MÊME liste de JVID que SSV / TSA
  // (tous les logements + l'adresse du ticket, qui porte le caddie d'un SDU), lue de la même façon. Hors du calcul
  // de capacité, à dessein : un ordre en cours n'est pas un « client connecté ».
  const openOrders = useOpenOrders(ssvTsaJvids);
  // N41 (07-10 / 08-10) — les logements de la MÊME adresse postale que le DMP range sous un AUTRE building ID :
  // une lecture à part, en tâche de fond comme les trois précédentes. Ils ne rejoignent PAS `mduApartments` — la
  // capacité, COCON et le formulaire d'ajout gardent le périmètre du bâtiment. Leurs ordres en cours sont lus à
  // part aussi : les ajouter à la liste ci-dessus relirait tout, et les caddies déjà affichés clignoteraient.
  // N45 — l'adresse que vise une demande « par adresse » : explicite sur un ticket à plusieurs adresses seulement ; un
  // ticket à une adresse garde ses demandes d'avant, sans `jvid`.
  const activeJvidParam = panels?.multiAddress ? panels.activeJvid ?? null : null;
  const sameAddress = useSameAddressUnits(id, ssvTsaJvids, activeJvidParam);
  const strangerJvids = useMemo(() => sameAddress.units.map((u) => u.jvid), [sameAddress.units]);
  const strangerOrders = useOpenOrders(strangerJvids);
  // La fiche SSV / TSA ouverte : UNE modale à la racine, comme la capacité. Les documents sont figés au clic.
  const [ssvTsaOpen, setSsvTsaOpen] = useState<{ label: string; docs: EnrichmentSsvTsa[] } | null>(null);
  // "Ask the operator" on a Save-as-Pending (user ask 2026-07-29): posted PUBLICLY on the ticket —
  // the remark field is internal and never reaches the operator.
  // No-address mode (jvid-less ticket): the "link an existing JVID" mini-form (user decision 2026-07-29).
  const [linkJvid, setLinkJvid] = useState('');
  const [linking, setLinking] = useState(false);
  // N28 — la confirmation « ancien JVID → nouveau », ouverte seulement quand le ticket en a déjà un.
  const [relinkConfirm, setRelinkConfirm] = useState(false);
  // No-address mode: ONE contact block (user ask 2026-07-30 — the separate question/hand-off boxes
  // read as two disconnected things): a single message that rides with WHICHEVER action is clicked —
  // public question to the operator, or internal hand-off note (escalate / Send to Build).
  const [noAddrMessage, setNoAddrMessage] = useState('');
  // « 🔒 Interne » toggle + @mentions (user ask 2026-07-31, e.g. reminding @Mathieu Cloos): the same
  // question button posts an INTERNAL note instead of the public operator comment when toggled.
  const [noAddrInternal, setNoAddrInternal] = useState(false);
  const [noAddrMentions, setNoAddrMentions] = useState<MentionUser[]>([]);
  // Same toggle for the NORMAL mode's Message field — only meaningful on a Save « En attente ».
  const [msgInternal, setMsgInternal] = useState(false);
  const [msgMentions, setMsgMentions] = useState<MentionUser[]>([]);
  const [askingOperator, setAskingOperator] = useState(false);
  async function handleAskOperator() {
    if (!id || !noAddrMessage.trim()) return;
    setAskingOperator(true);
    try {
      const fin = finalizeMentions(noAddrMessage.trim(), noAddrInternal ? noAddrMentions : []);
      const res = await askOperatorForTicket(id, fin.text, {
        internal: noAddrInternal,
        mentionedUserIds: fin.ids,
      });
      if (res.data.uniticketSync.commented) {
        toast.success(t(noAddrInternal ? 'tickets:validation.askOperatorSentInternal' : 'tickets:validation.askOperatorSent'));
        // N40 — la question rouvre un ticket résolu ou fermé : dire s'il est reparti, ou qu'il est resté résolu.
        reportTicketReopen(res.data.uniticketSync.reopen, panels?.ticket.ticketNumber ?? '', t);
        setNoAddrMessage('');
        setNoAddrMentions([]);
        await load();
      } else {
        toast.error(res.data.uniticketSync.error || t('tickets:validation.askOperatorSendError'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.askOperatorSendError'));
    } finally {
      setAskingOperator(false);
    }
  }
  async function handleLinkAddress() {
    if (!id || !linkJvid) return;
    setLinking(true);
    try {
      await apiFetch(`/tickets/${id}/link-address`, {
        method: 'POST',
        body: JSON.stringify({ jvid: Number(linkJvid) }),
      });
      toast.success(t('tickets:validation.noAddressLinkDone', { jvid: linkJvid }));
      // The ticket now has a jvid — a full reload reopens the window in its normal validation mode.
      window.location.reload();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.noAddressLinkError'));
      setLinking(false);
    }
  }
  // Toggle for the ticket-comment volet shown on the LEFT inside the per-apartment sheet — so the reviewer
  // can keep reading the operator's description (hidden behind the open drawer) while editing the mapping.
  const [showSiblingComment, setShowSiblingComment] = useState(false);
  const [history, setHistory] = useState<AuditLogItem[]>([]);
  // Live ticket id, readable from async callbacks: the auto-advance (goToNextTicket) keeps this page
  // MOUNTED while :id changes, so a fetch started for the PREVIOUS ticket can resolve after the next
  // ticket's own fetch and clobber its state (user case 2026-07-31: the freshly saved verdict of
  // UT-2026-000113 showed under « Other addresses of the building » on UT-2026-000118 until an F5).
  // Every async loader below drops its response when the id changed while it was in flight.
  const idRef = useRef(id);
  idRef.current = id;
  // N45 — même garde pour l'onglet : la réponse d'une adresse quittée ne doit jamais remplir la suivante.
  const jvidRef = useRef(urlJvid);
  jvidRef.current = urlJvid;
  const isShowing = (ticketId: string, jvid: number | null) => idRef.current === ticketId && jvidRef.current === jvid;
  // N45 — des panneaux déjà rendus par un geste (ajout, retrait, résolution) pour l'onglet où l'URL va : le chargement
  // les prend tels quels au lieu de relire le serveur.
  const handedPanels = useRef<{ id: string; jvid: number | null; data: AddressValidationPanelsResponse } | null>(null);
  // Le ticket dont les panneaux sont à l'écran : changer d'onglet n'y remet pas le squelette, l'adresse quittée reste
  // lisible (et inerte) le temps de lire la suivante.
  const shownTicket = useRef<string | null>(null);
  const [switching, setSwitching] = useState(false);

  function hydrate(data: AddressValidationPanelsResponse) {
    setPanels(data);
    // Snapshot of what Panel 2 was loaded with — the dirty-check behind its « Valider » button (F6).
    // Rebuilt below from the very values handed to setForm, so the two can never disagree.
    let loaded: Record<EditKey, string> | null = null;
    // A building with ≥2 dwellings IS multi-dwelling → the mapping's dwelling type must read MDU, even when
    // a single original address (the reference) still says SDU because a box was just added. This derived
    // value overrides the SDU pre-fill for the dwelling-type field only; the field stays editable so the
    // reviewer can still change it.
    //
    // Only the units IN THE DEPLOYMENT SCOPE count (Anouk, quality meeting 2026-08-10: « s'il compte toutes
    // les unités, ça va pas, il faut compter uniquement celles qui sont en scope »). Counting every sibling
    // announced MDU to Proximus for buildings where a single dwelling is actually deployed, and MDU breaks
    // their ordering — the 2026-08-11 audit found 4 addresses in that state, 3 of them contradicting the
    // DMP's own SDU. A sibling whose attributes could not be read (snapshot fallback / DMP unreachable) is
    // NOT counted: with nothing to answer with, the safe answer is "not MDU" rather than a guess.
    const buildingIsMdu = (data.mduApartments?.filter((a) => a.attributes?.scopelistV15).length ?? 0) >= 2;
    setForm({
      status: data.validation.status,
      remark: data.validation.remark ?? '',
      mapping: EDIT_FIELDS.reduce((acc, f) => {
        if (f.key === 'dwellingUnitType' && buildingIsMdu) {
          acc[f.key] = 'MDU';
        } else {
          // Saved mapping → the creator's encoded address (the reference snapshot) → blank. See prefill().
          acc[f.key] = prefill(
            data.mapping?.[f.key] as string | null,
            data.reference?.[f.key] as string | null | undefined,
          );
        }
        return acc;
      }, (loaded = {} as Record<EditKey, string>)),
    });
    setMappingBaseline(loaded);
    setMappingSaved(false);
  }

  const load = useCallback(async () => {
    if (!id) return;
    const jvid = urlJvid;
    const handed = handedPanels.current;
    handedPanels.current = null;
    if (handed && handed.id === id && handed.jvid === jvid) {
      hydrate(handed.data);
      shownTicket.current = id;
      setNotFound(false);
      setLoading(false);
      setSwitching(false);
      return;
    }
    const sameTicket = shownTicket.current === id;
    if (sameTicket) setSwitching(true);
    else setLoading(true);
    let redirected = false;
    try {
      const res = await fetchAddressValidation(id, jvid);
      if (!isShowing(id, jvid)) return; // stale response — we auto-advanced (or changed tab) meanwhile
      hydrate(res.data);
      shownTicket.current = id;
      setNotFound(false);
    } catch (err: any) {
      if (!isShowing(id, jvid)) return;
      if (err instanceof ApiError && err.status === 404 && err.body?.code === 'NOT_A_TICKET_ADDRESS') {
        // N45 — l'adresse de l'URL n'est plus une adresse du ticket (retirée depuis, lien ancien) : on rouvre la
        // principale, et on le dit, plutôt qu'un écran vide.
        redirected = true;
        toast.info(t('tickets:validation.multi.notInTicket', { jvid }));
        setSearchParams((prev) => withJvid(prev, null), { replace: true });
        return;
      }
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else toast.error(err.message || t('tickets:validation.loadError'));
    } finally {
      if (!redirected && isShowing(id, jvid)) { setLoading(false); setSwitching(false); }
    }
    // isShowing / hydrate lisent des refs et des setters : stables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, urlJvid, t]);

  const loadHistory = useCallback(async () => {
    if (!id) return;
    const jvid = urlJvid;
    try {
      const res = await fetchValidationHistory(id, jvid);
      if (!isShowing(id, jvid)) return; // stale response — see idRef
      setHistory(res.data);
    } catch { /* history is non-critical — keep the page usable */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, urlJvid]);

  useEffect(() => {
    // New ticket → wipe the previous ticket's history NOW (its section must never flash on the next one).
    setHistory([]);
    load();
    loadHistory();
  }, [load, loadHistory]);

  // Acquire the ticket's validation lock on mount + renew/retry every 60 s (TTL 3 min server-side):
  // while another user holds it the same call keeps polling, so the moment they leave (release or
  // TTL lapse) the next tick flips us to holder and the actions re-enable. Released on unmount
  // (in-app navigation) AND on pagehide (tab close / refresh) via a keepalive fetch; a missed
  // release is covered by the TTL.
  const tryAcquireLock = useCallback(async () => {
    if (!id) return;
    try {
      const res = await acquireValidationLock(id);
      setLock(res.data);
    } catch {
      // Transient failure (network / 404 on a vanished ticket): keep the last known state — the
      // server still enforces via 423 on every mutating route.
    }
  }, [id]);
  useEffect(() => {
    if (!id) return;
    tryAcquireLock();
    const heartbeat = setInterval(tryAcquireLock, 60_000);
    const onPageHide = () => releaseValidationLockOnLeave(id);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('pagehide', onPageHide);
      releaseValidationLockOnLeave(id);
    };
  }, [id, tryAcquireLock]);

  // Admin ghost-lock break: force-release whoever holds it, then take it.
  async function handleForceTakeover() {
    if (!id) return;
    try {
      await forceReleaseValidationLock(id);
      await tryAcquireLock();
      toast.success(t('tickets:validation.lock.forceTaken'));
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.lock.forceError'));
    }
  }

  // Compute the neighbouring tickets → the "Previous/Next ticket" buttons + every auto-advance, so a
  // validator can walk the inbox in both directions without going back to the list.
  // Reads the validator's CURRENT view (user ask 2026-08-04): this used to be hardcoded to PENDING, so
  // working the « À traiter » view threw you out of it at the first hand-off (« il m'a sorti de ma liste
  // à traiter »). Re-runs when the ticket changes so it advances after each navigation.
  // 2026-08-10 — the view was not enough: the chain now rebuilds the reviewer's list ENTIRELY (search,
  // « à moi », and the column sort replayed through the queue's own comparator). Anouk, l. 1508:
  // « à chaque fois que je faisais suivant, je tombais sur les tickets à moi […] il ne suit pas cette
  // liste-là, il suit celle d'origine avant les filtres » — her own files float to the top of the
  // SERVER order (F11) while her screen was sorted by date, so every hand-off threw her back onto them.
  // includeTest is now FALSE, matching the queue page's own call: it used to be `isAdmin`, and the flag
  // IS honoured server-side for admins — the chain could hold test rows the screen never showed.
  useEffect(() => {
    let alive = true;
    const queue = readQueueState();
    // ⭐ `favOwnerId` fait partie de l'état de la liste : sans lui, « Ticket suivant » repartirait de MES
    // favoris pendant que l'écran affiche ceux d'un collègue — exactement la famille de bug ci-dessus.
    fetchQueueForView(queue.view, false, queue.search, queue.mine, queue.favOwnerId)
      .then((res) => {
        if (!alive) return;
        // N45 — un ticket à plusieurs adresses a une ligne par adresse tant que la file ne les regroupe pas : sans
        // dédoublonnage, « Ticket suivant » retomberait sur le même ticket. Le premier rang de chacun fait foi.
        const ids = [...new Set(sortQueueRows(res.data, queue.sort)
          .map((i) => i.lastTicketId)
          .filter((x): x is string => !!x))];
        const idx = id ? ids.indexOf(id) : -1;
        // Both ends WRAP (user ask 2026-08-04: « je voudrais qu'on repasse à la 1ère au début ») and
        // never land on ourselves, so a list reduced to the current ticket leaves both buttons disabled
        // instead of self-navigating. A ticket that already left the view (verdict given, idx < 0)
        // falls through to the first row going forward and the last one going back.
        const firstOther = ids.find((x) => x !== id) ?? null;
        const lastOther = [...ids].reverse().find((x) => x !== id) ?? null;
        setNextTicketId((idx >= 0 ? ids[idx + 1] : undefined) ?? firstOther);
        setPrevTicketId((idx > 0 ? ids[idx - 1] : undefined) ?? lastOther);
      })
      .catch(() => { if (alive) { setNextTicketId(null); setPrevTicketId(null); } });
    return () => { alive = false; };
  }, [id]);

  // After a 409, advance the concurrency token + reference + banners WITHOUT resetting the reviewer's
  // in-progress form and WITHOUT swapping the edit-diff baseline (mergeRefreshedPanels keeps
  // prev.mapping) — so the next Save re-sends only the reviewer's own typed edits, never a phantom
  // edit that would silently revert a concurrent writer's change on the live DMP.
  const reconcilePanels = useCallback(async () => {
    if (!id) return;
    const jvid = urlJvid;
    try {
      const res = await fetchAddressValidation(id, jvid);
      if (!isShowing(id, jvid)) return;
      setPanels((prev) => (prev ? mergeRefreshedPanels(prev, res.data) : res.data));
    } catch { /* keep the current panels if the refresh fails */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, urlJvid]);

  async function handleRefresh() {
    if (!id) return;
    const jvid = urlJvid;
    setRefreshing(true);
    try {
      const res = await refreshAddressValidationReference(id, jvid);
      if (!isShowing(id, jvid)) return;
      // N45 — une adresse créée depuis ce ticket (menu « Gérer l'adresse ») s'y ajoute d'office : son onglet paraît
      // au rafraîchissement qui suit la création, et on le dit — sinon l'ajout passerait inaperçu.
      const known = new Set((panels?.addresses ?? []).map((a) => a.jvid));
      const joined = panels?.addresses ? (res.data.addresses ?? []).filter((a) => !known.has(a.jvid)) : [];
      // Advance the live reference, MDU list, concurrency token and banners but keep the reviewer's
      // in-progress edits and the edit-diff baseline (see mergeRefreshedPanels).
      setPanels((prev) => (prev ? mergeRefreshedPanels(prev, res.data) : res.data));
      if (joined.length) {
        toast.success(t('tickets:validation.multi.joined', {
          count: joined.length, label: joined.map(ticketAddressLabel).join(', '),
        }), { duration: 8000 });
      }
      // C0.1 : COCON se relit aussi (plan ③) — un voisin a pu prendre une fibre depuis l'ouverture. Quand la liste
      // de JVID change dans le même temps (boîte ajoutée), les deux mises à jour tombent dans un seul rendu :
      // une seule lecture. Même chose pour SSV / TSA, et pour les ordres en cours (N42).
      facadeCocon.retry();
      ssvTsa.retry();
      buildFiles.retry();
      openOrders.retry();
      sameAddress.retry();
    } catch (err: any) {
      toast.error(err.message || t('tickets:validation.loadError'));
    } finally {
      setRefreshing(false);
    }
  }

  // N29.1 — « Build a répondu » posé à la main par un admin : on reporte la réponse du serveur dans le bloc ticket,
  // sans rafraîchissement — c'est ce champ qui ouvre, dans les formulaires, la question PST bis.
  const markBuildRepliedLocally = (at: string) => setPanels((prev) => (prev
    ? { ...prev, ticket: { ...prev.ticket, buildRepliedAt: at, buildRepliedSource: 'MANUAL' } }
    : prev));

  // Panel 2 « Valider » (F6): write ONLY the mapping row, right now, with a visible outcome — the same
  // address_mapping_table upsert the per-apartment volet already uses (one write path, one audit shape).
  // The verdict Save keeps writing the same fields afterwards: re-sending identical values is a no-op
  // diff, so there is no double write. The panels are then refreshed the « Refresh from DMP » way, which
  // advances expectedMappingUpdatedAt WITHOUT touching the reviewer's in-progress edits — otherwise this
  // save would make the concurrency token stale and the next verdict Save would 409 against itself.
  async function handleSaveMappingOnly() {
    const jvid = reference?.jvid ?? mapping?.jvid ?? null;
    if (!id || !form || jvid == null || !mappingBaseline) return;
    if (!houseNumberDigitsOnly(form.mapping.houseNumber)) {
      blockSave(t('tickets:addressMgmt.create.errHouseNumberDigits'));
      return;
    }
    if (!houseNumberExtensionCoherent(form.mapping.houseNumber, form.mapping.houseNumberExtension)) {
      blockSave(t('tickets:validation.houseExtIncoherent'));
      return;
    }
    setSaveBlocked(null);
    setSavingMapping(true);
    try {
      // Field-level old→new diff for the audit trail, exactly like the volet's save.
      const edits: Record<string, { from: string | null; to: string | null }> = {};
      for (const f of EDIT_FIELDS) {
        if (form.mapping[f.key] !== mappingBaseline[f.key]) {
          edits[f.key] = { from: mappingBaseline[f.key] || null, to: form.mapping[f.key] || null };
        }
      }
      await upsertSiblingMapping(id, jvid, {
        addrStreet: form.mapping.streetName.trim() || null,
        addrHouseNumber: form.mapping.houseNumber.trim() || null,
        houseNumberExtension: form.mapping.houseNumberExtension.trim() || null,
        addrBusNumber: form.mapping.busNumber.trim() || null,
        addrPostalCode: form.mapping.postalCode.trim() || null,
        addrMunicipality: form.mapping.municipality.trim() || null,
        addrCity: form.mapping.city.trim() || null,
        addrDwellingUnitType: form.mapping.dwellingUnitType.trim() || null,
        ...(Object.keys(edits).length ? { edits } : {}),
      });
      setMappingBaseline({ ...form.mapping });
      setMappingSaved(true);
      toast.success(t('tickets:validation.mappingSaved'));
      loadHistory();
      try {
        const res = await refreshAddressValidationReference(id, urlJvid);
        if (isShowing(id, urlJvid)) setPanels((prev) => (prev ? mergeRefreshedPanels(prev, res.data) : res.data));
      } catch { /* the write landed; a failed token refresh only risks a 409 the Save already handles */ }
    } catch (err: any) {
      blockSave(err?.message || t('tickets:validation.mappingSaveError'));
    } finally {
      setSavingMapping(false);
    }
  }

  // After a Release / Un-release inside the per-apartment sheet, refresh the panels and RE-POINT
  // selectedSibling at the updated apartment (same jvid) WITHOUT closing the sheet — so the volet
  // re-renders in its new state (released → the mapping-edit form; un-released → the Release form),
  // letting the reviewer edit the mapping immediately after releasing.
  const refreshSelectedSibling = useCallback(async () => {
    if (!id) return;
    const jvid = selectedSibling?.jvid ?? null;
    const tab = urlJvid;
    try {
      const res = await fetchAddressValidation(id, tab);
      if (!isShowing(id, tab)) return;
      setPanels((prev) => (prev ? mergeRefreshedPanels(prev, res.data) : res.data));
      if (jvid != null) {
        // The sheet may be showing the ticket's OWN address (SDU path — a synthesized entry that is not
        // in mduApartments): re-synthesize it from the refreshed panels instead of closing the sheet.
        const found = res.data.mduApartments?.find((a) => a.jvid === jvid) ?? null;
        setSelectedSibling(
          found ?? (res.data.reference?.jvid === jvid ? currentAsApartment(res.data) : null),
        );
      }
    } catch {
      /* keep the sheet open with the current data on failure */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, urlJvid, selectedSibling]);

  // D2708-2 (décision user 2026-10-15, ticket …001113) — dans la fiche par logement, sur un MDU SEULEMENT, la ligne
  // rouge d'un identifiant réseau porte le « Déduire du bâtiment » de la console : le release refusait la boîte sans
  // PoP, et rien ici ne permettait de le poser. Sur un SDU, un PoP absent signale le plus souvent une adresse hors
  // scope (Anouk, 08-10) : aucun bouton, le refus du release dit d'escalader. Même source du type que le release.
  // 22-10 — un FAUX SDU (SDU dans le DMP, mais une autre unité de la même adresse est déjà releasée : règle d'Anouk)
  // est un MDU pour cette réparation ; le serveur le release d'ailleurs en MDU.
  const sheetIsMdu = String(selectedSibling?.reference?.dwellingUnitType ?? '').trim().toUpperCase() === 'MDU'
    || selectedSibling?.attributes?.fakeSdu === true;
  const { renderFix: renderSheetNetworkFix } = useServiceFileFixes(
    sheetIsMdu ? selectedSibling?.jvid ?? null : null,
    selectedSibling?.attributes ?? null,
    () => { void refreshSelectedSibling(); },
    null,
    { only: NETWORK_ID_CHECKS },
  );

  // Surface the best-effort Uniticket write-back outcome (3.1/3.3/3.4) as a secondary toast, on top of
  // the primary local-save toast. Silent when writes are disabled and nothing was attempted is not the
  // case here — we tell the reviewer so they know the verdict did NOT propagate to Uniticket.
  function reportSync(sync: UniticketWriteResult | null | undefined) {
    if (!sync) return;
    if (sync.enabled === false) { toast.message(t('tickets:validation.syncDisabled')); return; }
    // Most significant positive outcome (resolve > reassign > comment), then any error on top.
    if (sync.resolved) toast.success(t('tickets:validation.syncResolved'));
    else if (sync.reassigned) toast.success(t('tickets:validation.syncReassigned'));
    else if (sync.commented) toast.success(t('tickets:validation.syncCommented'));
    if (sync.error) toast.error(t('tickets:validation.syncFailed', { error: sync.error }), { duration: 8000 });
  }

  // Advance to the next ticket of the CURRENT view — reuses the same computed `nextTicketId` as the
  // "Next ticket" button (which now wraps to the first row at the end of the list). When there's none,
  // stay put (the button is likewise disabled).
  const goToNextTicket = () => {
    if (nextTicketId) navigate(`/tickets/${nextTicketId}/address-validation`);
  };
  // N43 ③ (décision du user du 08-10) — un passage AUTOMATIQUE au ticket suivant doit se VOIR. Le 05-10, après un
  // enregistrement, l'app a changé de ticket sans que personne le remarque, et le commentaire a été cherché sur le
  // mauvais dossier. Le message du geste (`done` : enregistré, escaladé, renvoyé, envoyé chez Build) devient donc
  // « … — ticket suivant ouvert », avec de quoi REVENIR au ticket quitté — par son URL, donc même s'il vient de
  // sortir de la vue. UN seul message : il REMPLACE celui du geste (`gestureToast`, l'identifiant que sonner a rendu
  // pour lui) au lieu de s'y ajouter. Remplacer = retirer l'ancien et en poser un NEUF, pas réécrire l'ancien sur
  // place : sonner empile, le plus récent devant — réécrit sur place, le message resterait DERRIÈRE celui de la
  // synchro Uniticket, arrivé entre-temps (vu à l'écran le 08-10), c'est-à-dire invisible. Les messages de la
  // synchro et les erreurs, eux, sont d'autres messages et restent dans la pile. Sans ticket suivant on le dit
  // aussi, sinon « rien ne se passe » se lirait comme une panne. Le bouton « Ticket suivant » reste silencieux :
  // celui qui clique sait ce qu'il fait.
  //
  // N40 — `reopen` : ce que le geste a fait d'un ticket résolu ou fermé (absent le reste du temps).
  //  · rouvert : la phrase voyage DANS ce message (sa seconde ligne). À part, elle resterait derrière lui dans la
  //    pile, illisible (vu à l'écran le 08-10), alors que les deux doivent se lire ensemble ;
  //  · RESTÉ résolu (refus d'Uniticket, écriture coupée) : on ne quitte PAS le dossier — le relecteur a quelque
  //    chose à y faire (le rouvrir dans Uniticket), et l'avertissement, posé en dernier, reste devant. Même règle
  //    que pour une pièce jointe qui n'est pas partie : un échec retient le passage au ticket suivant.
  const advanceAfter = (done: string, gestureToast: string | number, reopen?: UniticketReopenReport) => {
    const left = { id, number: panels?.ticket.ticketNumber ?? '' };
    if (reopenFailed(reopen)) { reportTicketReopen(reopen, left.number, t); return; }
    const description = reopenSentence(reopen, left.number, t) ?? undefined;
    toast.dismiss(gestureToast);
    if (!nextTicketId) {
      toast.success(t('tickets:validation.autoNextNone', { done }), { duration: 8000, description });
      return;
    }
    toast.success(t('tickets:validation.autoNextOpened', { done }), {
      description,
      // Assez longtemps pour lire, comprendre qu'on a changé de dossier, et cliquer.
      duration: 15000,
      ...(left.id ? {
        action: {
          label: t('tickets:validation.autoNextBack', { ticket: left.number }),
          onClick: () => navigate(`/tickets/${left.id}/address-validation`),
        },
      } : {}),
    });
    navigate(`/tickets/${nextTicketId}/address-validation`);
  };
  // Manual only — nothing auto-advances backwards.
  const goToPrevTicket = () => {
    if (prevTicketId) navigate(`/tickets/${prevTicketId}/address-validation`);
  };

  // ── N45 — les onglets d'un ticket à plusieurs adresses ──────────────────────────────────────────────────────────
  // Changer d'onglet = changer `?jvid=` (sans entrée d'historique : le bouton « retour » du navigateur sauterait la
  // confirmation ci-dessous) ; l'effet de chargement lit l'adresse. Défaut ⓔ du plan : une correction, une note ou
  // un verdict non enregistrés demandent confirmation avant de partir.
  const [pendingTab, setPendingTab] = useState<number | null>(null);
  const hasUnsavedWork = () => !!form && !!panels && (
    (mappingBaseline != null && EDIT_FIELDS.some((f) => form.mapping[f.key] !== mappingBaseline[f.key]))
    || form.remark.trim() !== (panels.validation.remark ?? '').trim()
    || form.status !== panels.validation.status
  );
  function openTab(jvid: number) {
    const primary = panels?.addresses?.find((a) => a.primary)?.jvid ?? null;
    setSearchParams((prev) => withJvid(prev, jvid === primary ? null : jvid), { replace: true });
  }
  function selectTab(jvid: number) {
    if (!panels || jvid === panels.activeJvid) return;
    if (hasUnsavedWork()) { setPendingTab(jvid); return; }
    openTab(jvid);
  }
  // Une réponse qui porte déjà la fenêtre voulue (ajout, retrait, résolution) : affichée telle quelle, l'URL suit
  // son onglet sans relire le serveur.
  function showPanels(data: AddressValidationPanelsResponse) {
    if (!id) return;
    const target = jvidParamOf(data);
    if (target === urlJvid) { hydrate(data); return; }
    handedPanels.current = { id, jvid: target, data };
    setSearchParams((prev) => withJvid(prev, target), { replace: true });
  }
  // Après un verdict final sur une adresse : l'adresse suivante qui n'en a pas (dans l'ordre des onglets, en
  // revenant au début), sinon on descend à « Résoudre le ticket ». Le ticket, lui, ne change pas : il n'est pas fini.
  function afterAddressVerdict(data: AddressValidationPanelsResponse, gestureToast: string | number) {
    const list = data.addresses ?? [];
    const at = list.findIndex((a) => a.jvid === data.activeJvid);
    const next = [...list.slice(at + 1), ...list.slice(0, Math.max(at, 0))]
      .find((a) => !isFinalAddressStatus(a.status) && !a.ownedByOtherTicket);
    toast.dismiss(gestureToast);
    if (next) {
      toast.success(t('tickets:validation.multi.nextTabOpened', { label: ticketAddressLabel(next) }), { duration: 8000 });
      openTab(next.jvid);
      return;
    }
    toast.success(t('tickets:validation.multi.allDone'), { duration: 10000 });
    requestAnimationFrame(() => document.getElementById('av-resolve')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  }

  // Refuse the save and SAY WHY, twice over: a toast for the eye, and a persistent block above the
  // buttons for the reader. Every early return below goes through here — a silent refusal is the bug
  // we are fixing (user ask 2026-08-04).
  function blockSave(message: string, duration = 8000): void {
    setSaveBlocked(message);
    toast.error(message, { duration });
  }
  // Cleared at the start of every new attempt (see handleSave / performSave) and when the window
  // moves to another ticket. Deliberately NOT cleared on every `form` change: the 409 path calls
  // reconcilePanels() — which rebuilds form state — right before blocking, so a clear-on-form-change
  // effect would swallow the very message that path exists to show.
  useEffect(() => { setSaveBlocked(null); }, [id]);

  // Un changement de ticket remet à zéro TOUT ce qui appartenait au précédent (demande user 2026-08-28).
  // Le déclencheur : la case « 🔒 Interne » restait cochée d'un ticket à l'autre. « Suivant » ne démonte
  // pas la page — il ne change que le `:id` de la route — donc l'état local traverse, et une question
  // publique destinée à Proximus pouvait partir en note interne sans que personne ne le voie.
  // La liste est volontairement exhaustive : le bug n'était pas cette case, c'était l'absence de règle.
  // (`form` est déjà réinitialisé par hydrate(), et `history` par l'effet de chargement.)
  useEffect(() => {
    setMsgInternal(false);
    setMsgMentions([]);
    setNoAddrInternal(false);
    setNoAddrMentions([]);
    setNoAddrMessage('');
    setBuildQuestion('');
    setLinkJvid('');
    setSelectedSibling(null);
    // Une pièce jointe choisie et non envoyée suivrait sur le dossier suivant : performAttachmentUpload
    // ne la libère qu'en cas de SUCCÈS, et un échec laisse le relecteur sur place avec le fichier armé.
    setAttachFile(null);
  }, [id]);

  // N45 — changer d'onglet ferme ce qui appartenait à l'adresse quittée (sa fiche de logement, ses détails, le refus
  // affiché sous son verdict). Le composeur du ticket et la pièce jointe, eux, sont au TICKET : ils restent.
  useEffect(() => {
    setSelectedSibling(null);
    setCapacityGroup(null);
    setSsvTsaOpen(null);
    setSaveBlocked(null);
    setMsgMentions([]);
  }, [urlJvid]);

  // N29.4 (décisions du user, 30-09) — « Non » à « Build a-t-il confirmé ? » : le formulaire n'a RIEN créé. On
  // prépare le refus sans rien envoyer — le validateur relit, puis décide : sur un ticket lié, le verdict « Rejeté »
  // et son motif ; sans JVID (pas de verdict possible), le message public à l'opérateur, qu'il envoie par « Poser la
  // question ». Un texte déjà tapé n'est jamais écrasé.
  function prepareBuildRefusal() {
    if (!panels) return;
    const remark = t('tickets:addressMgmt.pstBis.refusalRemark');
    if (panels.noAddress) {
      setNoAddrInternal(false);
      setNoAddrMessage((prev) => (prev.trim() ? prev : remark));
      toast.info(t('tickets:addressMgmt.pstBis.refusalPreparedMessage'));
    } else {
      setForm((f) => (f ? { ...f, status: 'REJECTED', remark: f.remark.trim() ? f.remark : remark } : f));
      toast.info(t('tickets:addressMgmt.pstBis.refusalPreparedVerdict'));
    }
    const target = panels.noAddress ? 'av-no-address-contact' : 'av-verdict';
    requestAnimationFrame(() => document.getElementById(target)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  }

  // The actual save (called directly for Validated/Rejected, or via the Pending-guard modal's confirm).
  async function performSave(statusOverride?: AddressValidationStatus) {
    if (!id || !panels || !form) return;
    // statusOverride: the reject guard's « ask instead » answers for the reviewer without waiting for the
    // form state to settle — setForm is async, so reading form.status here would still say REJECTED.
    const verdict = statusOverride ?? form.status;
    // N45 — ticket à plusieurs adresses : le verdict vise l'onglet ouvert, n'écrit rien dans Uniticket, et la question
    // à l'opérateur se pose pour TOUT le ticket (carte « Le ticket entier ») — jamais depuis une adresse.
    const multi = panels.multiAddress === true;
    setSaveBlocked(null);
    setSaving(true);
    try {
      const edits: Partial<Record<EditKey, string>> = {};
      for (const f of EDIT_FIELDS) {
        const original = (panels.mapping?.[f.key] as string | null) ?? '';
        if (form.mapping[f.key] !== original) edits[f.key] = form.mapping[f.key];
      }
      // …et ce que le relecteur a réellement TAPÉ, mesuré contre la photo des valeurs chargées dans le
      // formulaire. Le diff ci-dessus se fait contre la LIGNE DE MAPPING : sans ligne au chargement,
      // tout le pré-remplissage part comme une « modification » et écrase une ligne créée entre-temps
      // par la fiche d'appartement (incident jvid 13181765 du 30-07 : la boîte corrigée en « 5 » est
      // repassée à « Bt5 » 36 secondes plus tard). Toujours envoyé, même vide — c'est justement le cas
      // vide qui doit empêcher l'écrasement.
      const touched = mappingBaseline
        ? EDIT_FIELDS.filter((f) => form.mapping[f.key] !== mappingBaseline[f.key]).map((f) => f.key)
        : Object.keys(edits);
      // ONE message field (user rule 2026-07-30: nothing written may stay app-internal): stored as
      // the remark for the history AND, on a PENDING save, ALSO posted publicly as the operator
      // question (server-side round-trip). Verdicts already carry it publicly (resolution/rejection).
      const message = form.remark.trim();
      // The stored remark keeps the READABLE "@Name" text; only the posted question carries the
      // native "@{uuid}" tokens (+ the ids for the future notification support).
      const finQ = !multi && verdict === 'PENDING' && message
        ? finalizeMentions(message, msgInternal ? msgMentions : [])
        : null;
      const res = await saveAddressValidation(id, {
        ...(activeJvidParam != null ? { jvid: activeJvidParam } : {}),
        status: verdict,
        remark: message ? message : null,
        ...(Object.keys(edits).length ? { mappingEdits: edits, mappingTouched: touched } : {}),
        expectedMappingUpdatedAt: panels.expectedMappingUpdatedAt,
        ...(finQ ? {
          operatorQuestion: finQ.text,
          ...(msgInternal ? { operatorQuestionInternal: true } : {}),
          ...(finQ.ids.length ? { operatorQuestionMentions: finQ.ids } : {}),
        } : {}),
      });
      setMsgMentions([]);
      hydrate(res.data);
      loadHistory();
      const savedToast = toast.success(t('tickets:validation.saved'));
      reportSync(res.uniticketSync);
      setThreadVersion((v) => v + 1); // the save may have posted (resolution/rejection/question)
      // Reviewer attachment: pushed AFTER the save so a failed upload never blocks the verdict. On
      // failure the file stays selected and we stay on the page so the reviewer can retry (or drop it).
      let uploadOk = true;
      if (attachFile) uploadOk = await performAttachmentUpload(attachFile);
      if (multi) {
        // N45 — on ne quitte pas un ticket pas fini : adresse suivante, ou « Résoudre le ticket ».
        if (verdict !== 'PENDING' && uploadOk) afterAddressVerdict(res.data, savedToast);
        else reportTicketReopen(res.uniticketSync?.reopen, panels.ticket.ticketNumber, t);
        return;
      }
      // Auto-advance on a final verdict — and (user rule 2026-07-31) on a Save-as-Pending whose
      // question WAS posted: the ticket just parked in « En attente de réponse », nothing keeps the
      // reviewer here. A failed post (commented ≠ true) stays put so the question can be retried.
      const questionParked = verdict === 'PENDING' && finQ != null && res.uniticketSync?.commented === true;
      // N40 — ce que le geste a fait d'un ticket résolu ou fermé se dit avec le passage au ticket suivant, ou
      // seul (et en dernier, donc devant) quand on reste sur le dossier.
      const reopen = res.uniticketSync?.reopen;
      if ((verdict !== 'PENDING' || questionParked) && uploadOk) advanceAfter(t('tickets:validation.saved'), savedToast, reopen);
      else reportTicketReopen(reopen, panels.ticket.ticketNumber, t);
    } catch (err: any) {
      if (err instanceof ApiError && typeof err.body?.code === 'string') {
        // N45 — un refus NOMMÉ (le ticket attend un envoi, l'adresse n'est plus du ticket…) : rien n'a bougé sous
        // le formulaire, ce n'est pas un conflit de mapping — on dit pourquoi, sans rien relire.
        blockSave(ticketAddressErrorText(err, t), 10000);
      } else if (err instanceof ApiError && err.status === 409) {
        // The row moved under us; nothing was written. Refresh the concurrency token + reference but
        // KEEP the reviewer's typed verdict/corrections so they aren't silently lost — they re-check
        // against the updated reference and Save again.
        await reconcilePanels();
        blockSave(t('tickets:validation.conflictKept'), 10000);
      } else if (err instanceof ApiError && err.status === 400) {
        // 400 = a required address field left blank (zod .min(1)) OR a rejection without a reason remark
        // (the route refines remark for REJECTED). The server message NAMES the offending field
        // ("path: message") — surface it verbatim instead of the old generic text, which was the whole
        // point of the 2026-08-04 ask. Fall back to the generic wording if the body carried none.
        blockSave(err.message?.trim() ? err.message : t('tickets:validation.requiredFieldEmpty'));
      } else {
        blockSave(err.message || t('tickets:validation.saveError'));
      }
    } finally {
      setSaving(false);
    }
  }

  // Upload the selected reviewer file onto the Uniticket ticket. true = success (file cleared +
  // attachments card refreshed); false keeps the file selected so the reviewer can retry.
  async function performAttachmentUpload(file: File): Promise<boolean> {
    if (!id) return false;
    try {
      await uploadTicketAttachment(id, file);
      setAttachFile(null);
      setAttachmentsVersion((v) => v + 1);
      toast.success(t('tickets:validation.attachments.uploaded'));
      return true;
    } catch (err: any) {
      // Same rule as a refused save: the reviewer must SEE why, and the file stays attached so the
      // retry is one click away.
      blockSave(t('tickets:validation.attachments.uploadFailed', { error: err?.message ?? '' }));
      return false;
    }
  }

  // Save button gate: Pending isn't a final verdict → warn first (3.2, soft); Rejected needs a reason
  // remark (3.4 — also disables the button, this is defense-in-depth).
  function handleSave() {
    if (!form) return;
    if (!houseNumberDigitsOnly(form.mapping.houseNumber)) {
      blockSave(t('tickets:addressMgmt.create.errHouseNumberDigits'));
      return;
    }
    if (!houseNumberExtensionCoherent(form.mapping.houseNumber, form.mapping.houseNumberExtension)) {
      blockSave(t('tickets:validation.houseExtIncoherent'));
      return;
    }
    // N45 — le ticket entier attend (Build, l'opérateur, une escalade) : aucune adresse ne prend de verdict d'ici là.
    // Le serveur refuse aussi ; on le dit avant l'aller-retour, avec ce que le ticket attend.
    const waiting = panels?.multiAddress ? ticketWaitingFor(panels.ticket) : null;
    if (waiting && (form.status === 'VALIDATED' || form.status === 'REJECTED')) {
      blockSave(t(`tickets:validation.multi.verdictWaiting.${waiting}`));
      return;
    }
    if (form.status === 'PENDING') { setSaveBlocked(null); setPendingGuardOpen(true); return; }
    if (form.status === 'REJECTED' && !form.remark.trim()) {
      blockSave(t('tickets:validation.requiredRemarkRejected'));
      return;
    }
    // Reject guard (user ask 2026-08-28). Since the same day a rejection also RESOLVES the ticket in
    // Uniticket, so the button became final in a way it never was before. And it was being misused: of
    // the 17 rejections in production, 15 were plainly QUESTIONS (« pouvez-vous me communiquer les
    // coordonnées Lambert72 ? »), not refusals — the reviewers had no other one-click way to write to
    // the operator, so they took this one. The dialog therefore does more than warn: it offers the
    // action they actually wanted. There is a SINGLE message field, posted as the rejection reason on a
    // REJECTED save and as a public question on a PENDING one, so « ask instead » is literally the same
    // text under another verdict — nothing to retype, nothing to copy.
    // N45 — pas sur un ticket à plusieurs adresses : le rejet d'UNE adresse ne résout rien, et « demander plutôt »
    // poserait la question depuis une adresse, ce qui se fait pour tout le ticket.
    if (form.status === 'REJECTED' && !panels?.multiAddress) { setSaveBlocked(null); setRejectGuardOpen(true); return; }
    setSaveBlocked(null);
    performSave();
  }

  // "À traiter par X" (formerly "Escalate" — renamed to avoid colliding with Uniticket's own
  // Escalate): marks the row ESCALATED locally, posts an "À traiter par <target>" comment on the
  // Uniticket ticket and — with the v1.7.0 master key — really reassigns it. Styled confirm first.
  function handleEscalate() {
    if (!id || !form || !panels?.escalationTarget) return;
    setConfirmOpen('escalate');
  }
  async function doEscalate() {
    if (!id || !form || !panels?.escalationTarget) return;
    setEscalating(true);
    try {
      const res = await escalateAddressValidation(id, form.remark.trim() ? form.remark.trim() : null);
      hydrate(res.data);
      loadHistory();
      const escalatedToast = toast.success(t('tickets:validation.escalated'));
      reportSync(res.uniticketSync);
      setThreadVersion((v) => v + 1);
      // Same attachment rule as Save (user bug 2026-08-04, UT-2026-000506): the file picker sits above
      // ALL THREE buttons, so it must follow whichever one is pressed. Pushed AFTER the hand-off so a
      // failed upload never undoes it; a failure holds the auto-advance so the file isn't lost with the
      // unmounted page.
      const uploadOk = attachFile ? await performAttachmentUpload(attachFile) : true;
      if (uploadOk) advanceAfter(t('tickets:validation.escalated'), escalatedToast, res.uniticketSync?.reopen);
      else reportTicketReopen(res.uniticketSync?.reopen, panels.ticket.ticketNumber, t);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.escalateError'));
    } finally {
      setEscalating(false);
    }
  }

  // « Renvoyer » (user ask 2026-08-06, F4): the escalation recipient hands the file BACK to whoever
  // escalated it, with the answer. The row returns to PENDING assigned to that reviewer, so it lands at
  // the top of THEIR queue — and, thanks to the assignment rule, only theirs. Same message field and
  // same attachment rule as the other hand-offs.
  async function doReturnEscalation() {
    if (!id || !form || !panels?.returnTarget) return;
    setReturning(true);
    try {
      const res = await returnEscalatedValidation(id, form.remark.trim() ? form.remark.trim() : null);
      hydrate(res.data);
      loadHistory();
      const returned = t('tickets:validation.returned', { label: panels.returnTarget.label });
      const returnedToast = toast.success(returned);
      reportSync(res.uniticketSync);
      setThreadVersion((v) => v + 1);
      const uploadOk = attachFile ? await performAttachmentUpload(attachFile) : true;
      if (uploadOk) advanceAfter(returned, returnedToast, res.uniticketSync?.reopen);
      else reportTicketReopen(res.uniticketSync?.reopen, panels.ticket.ticketNumber, t);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.returnError'));
    } finally {
      setReturning(false);
    }
  }

  // Ticket-level hand-offs (no-address mode — no validation row): same confirms, distinct endpoints.
  async function doTicketEscalate() {
    if (!id) return;
    setEscalating(true);
    try {
      const res = await ticketEscalate(id, noAddrMessage.trim() || null);
      toast.success(t('tickets:validation.escalated'));
      reportSync(res.data.uniticketSync);
      reportTicketReopen(res.data.uniticketSync.reopen, panels?.ticket.ticketNumber ?? '', t);
      setThreadVersion((v) => v + 1);
      setNoAddrMessage('');
      await load();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.escalateError'));
    } finally {
      setEscalating(false);
    }
  }
  /** Hand a no-jvid ticket BACK down to the reviewer who escalated it (F4, completed 07-08). Mirrors
   *  doReturnEscalation; the message composer of the no-address mode carries the answer. */
  async function doTicketReturnEscalation() {
    if (!id || !panels?.returnTarget) return;
    setReturning(true);
    try {
      const res = await ticketReturnEscalation(id, noAddrMessage.trim() || null);
      toast.success(t('tickets:validation.returned', { label: panels.returnTarget.label }));
      reportSync(res.data.uniticketSync);
      reportTicketReopen(res.data.uniticketSync.reopen, panels.ticket.ticketNumber, t);
      setThreadVersion((v) => v + 1);
      setNoAddrMessage('');
      await load();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.returnError'));
    } finally {
      setReturning(false);
    }
  }
  async function doTicketSendToBuild() {
    if (!id) return;
    setSendingToBuild(true);
    try {
      const res = await ticketSendToBuild(id, noAddrMessage.trim() || null);
      toast.success(t('tickets:validation.sentToBuild'));
      reportSync(res.data.uniticketSync);
      reportTicketReopen(res.data.uniticketSync.reopen, panels?.ticket.ticketNumber ?? '', t);
      setThreadVersion((v) => v + 1);
      setNoAddrMessage('');
      await load();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.sendToBuildError'));
    } finally {
      setSendingToBuild(false);
    }
  }

  // Send to Build = a DISTINCT action (like Escalate): asks the admin-configured Build target for an
  // opinion (posts a Uniticket comment when writes are on + really reassigns via the v1.7.0 master key)
  // and parks the ticket as WAITING_BUILD. The reviewer keeps it — closure stays free. Styled confirm first.
  function handleSendToBuild() {
    if (!id || !form || !panels?.buildTarget) return;
    setConfirmOpen('build');
  }
  async function doSendToBuild() {
    if (!id || !form || !panels?.buildTarget) return;
    setSendingToBuild(true);
    try {
      const res = await sendToBuildAddressValidation(
        id,
        form.remark.trim() ? form.remark.trim() : null,
        buildQuestion.trim() ? buildQuestion.trim() : null,
      );
      setBuildQuestion('');
      hydrate(res.data);
      loadHistory();
      const sentToast = toast.success(t('tickets:validation.sentToBuild'));
      reportSync(res.uniticketSync);
      setThreadVersion((v) => v + 1);
      // Same attachment rule as Save — see doEscalate. This is the case that bit us: the Build note
      // said « réfère-toi à la pièce jointe » while the file had never left the browser.
      const uploadOk = attachFile ? await performAttachmentUpload(attachFile) : true;
      if (uploadOk) advanceAfter(t('tickets:validation.sentToBuild'), sentToast, res.uniticketSync?.reopen);
      else reportTicketReopen(res.uniticketSync?.reopen, panels.ticket.ticketNumber, t);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.sendToBuildError'));
    } finally {
      setSendingToBuild(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      </div>
    );
  }

  if (notFound || !panels || !form) {
    return (
      <div className="space-y-4">
        <Link to={id ? `/tickets/${id}` : '/tickets'} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-rose">
          <ArrowLeft className="w-4 h-4" /> {t('tickets:validation.backToTicket')}
        </Link>
        <GlassCard className="p-12 text-center text-slate-400">
          {t('tickets:validation.noValidation')}
        </GlassCard>
      </div>
    );
  }

  const { ticket, reference, mapping, mduApartments, referenceAttributes } = panels;
  // N45 — ticket à plusieurs adresses : un onglet par adresse, un verdict par adresse, et tout ce qui part vers
  // Uniticket (question, Build, escalade, résolution) se fait pour le ticket entier. Un ticket à une adresse ne change pas.
  const multi = panels.multiAddress === true;
  const primaryJvid = panels.addresses?.find((a) => a.primary)?.jvid ?? null;
  const onPrimaryTab = !multi || panels.activeJvid == null || panels.activeJvid === primaryJvid;
  const activeAddress = panels.addresses?.find((a) => a.jvid === panels.activeJvid) ?? null;
  // Les envois se font au niveau du TICKET : sans adresse (rien d'autre n'existe), ou pour toutes ses adresses.
  const ticketLevel = panels.noAddress === true || multi;
  const waitingFor = multi ? ticketWaitingFor(ticket) : null;
  // N28 (2026-09-22) — la fenêtre doit DIRE pourquoi la fiche est vide, et offrir une sortie. Le cas visé
  // est celui du user, récurrent : le ticket porte un jvid (UT-2026-000129 / 10328760) dont AUCUNE ligne
  // du DMP ne répond. Le panneau mapping n'y sert à rien puisqu'il se pré-remplit depuis la référence :
  // on rend à sa place le bloc de liaison DÉJÀ codé pour les tickets sans jvid.
  //
  // Une adresse ARCHIVÉE est volontairement HORS de cette bascule : depuis B-6 sa fiche n'est plus vide
  // (on y substitue la ligne archivée du DMP, grisée, avec sa date), et son panneau mapping porte la
  // demande réelle de l'opérateur — la cacher coûterait plus qu'elle ne rapporte. Le recours « relier
  // une autre adresse » pour ce cas reste à trancher avec le user.
  //
  // Un ticket normal ne bascule donc JAMAIS ici — c'est le garde-fou nommé par le plan.
  // N45 : seulement sur l'onglet de l'adresse principale — « Lier » remplace le JVID du TICKET ; une adresse ajoutée
  // qui ne répond plus se retire de son onglet (×), elle ne se relie pas.
  const canLink = panels.noAddress === true || (panels.referenceState === 'missing' && onPrimaryTab);
  // L'ancien jvid, pour la confirmation. `null` en mode sans-jvid : il n'y a rien à remplacer, donc pas
  // de fenêtre de confirmation — le geste d'origine n'en avait pas, et n'en a pas besoin.
  const relinkFrom = panels.noAddress ? null : (reference?.jvid ?? mapping?.jvid ?? null);
  const isDiff = (key: EditKey) =>
    (form.mapping[key] || '').trim() !== (((reference?.[key] as string | null) ?? '') || '').trim();
  // 3.4: a rejection must carry a reason (posted back to the operator) → block Save until filled.
  const rejectNeedsRemark = form.status === 'REJECTED' && !form.remark.trim();
  // House number vs extension must agree (the extension's leading digits = the house number; "7"+"7a" ok,
  // "7"+"8a" not) → block Save + flag the two fields. Mirrors the server guard in saveValidation.
  const houseExtIncoherent = !houseNumberExtensionCoherent(form.mapping.houseNumber, form.mapping.houseNumberExtension);
  // Digits-only house number (user rule 2026-07-31 — dup 626/633: « 7C » typed into Panel 2's House
  // no. showed no warning): flags the field, blocks Save, mirrored by the saveValidation server guard.
  const mappingHouseNumNotDigits =
    (form.mapping.houseNumber || '').trim() !== '' && !houseNumberDigitsOnly(form.mapping.houseNumber);
  // The building's apartments grouped by distinct street address (>1 ⇒ show sub-headers + a banner).
  const mduAddressGroups = mduApartments ? groupApartmentsByAddress(mduApartments) : [];
  // N38 (décision du 08-10) — les logements de la fenêtre que l'opérateur voit à la MÊME adresse (sa déclaration,
  // sinon le DMP : rue + numéro + extension + boîte). Calculé à l'écran sur la liste déjà chargée, aucune requête.
  // N41 ⑤ — la liste s'élargit aux logements de la même adresse postale rangés sous un autre building ID : pour
  // l'opérateur ce sont des logements de la même adresse, un doublon avec eux en est un.
  const strangers = sameAddress.units;
  const seenUnits = [...(mduApartments ?? []), ...strangers];
  const declaredDuplicates = findDeclaredDuplicates(seenUnits);
  // … et la même question posée à la SAISIE en cours du panneau 2 (le mapping du logement du ticket) : le message
  // paraît pendant la frappe, sans rien bloquer — « Valider » comme le verdict restent possibles.
  const ownJvid = reference?.jvid ?? mapping?.jvid ?? null;
  const mappingTwins = ownJvid != null
    ? duplicatesOfInput(seenUnits, ownJvid, {
      streetName: form.mapping.streetName,
      houseNumber: form.mapping.houseNumber,
      houseNumberExtension: form.mapping.houseNumberExtension,
      busNumber: form.mapping.busNumber,
    })
    : [];
  // N41 ③ — sous quelle adresse du tableau chaque logement « d'ailleurs » s'affiche : celle du logement de la
  // fenêtre auquel le serveur l'a accroché. Ceux qui ne trouvent pas leur adresse dans le tableau (pas de tableau :
  // un logement seul dans son bâtiment) vont dans une carte à eux.
  const strangersOf = (rows: MduApartmentDto[]) => strangers.filter((u) => rows.some((a) => a.jvid === u.anchorJvid));
  const placedStrangers = new Set(mduAddressGroups.flatMap((g) => strangersOf(g.rows).map((u) => u.jvid)));
  const looseStrangers = strangers.filter((u) => !placedStrangers.has(u.jvid));
  // Is the ticket's OWN address visible to the partner? approved_for_release is the flag that decides it —
  // NOT `status`, which a create derives from the delivery code and so reads HOMES_PASSED on a brand-new,
  // never-released address (user bug 2026-08-06). Drives the Panel-1 button wording, its amber "not
  // released yet" signalling (F8) and the pre-verdict reminder below.
  const sduReleased =
    referenceAttributes?.approvedForRelease === true || referenceAttributes?.homeReleased === true;
  // Panel 2 has unsaved text edits → offer « Valider » (F6).
  const mappingDirty = mappingBaseline != null
    && EDIT_FIELDS.some((f) => form.mapping[f.key] !== mappingBaseline[f.key]);

  // PST marker (user choice 2026-07-29, "mark the exception"): most existing addresses ARE in a Build
  // PST CSV, so in-PST rows show nothing — only a jvid absent from every PST (a candidate for the
  // Extra JVID file) gets a small amber dot, tooltip explains. Hidden while the local PST index has
  // never been built (pstIndexReady=false) so an empty index doesn't dot every address.
  const renderPstDot = (file: string | null | undefined, className?: string) => {
    if (!panels.pstIndexReady || file) return null;
    return (
      <span
        className={cn('inline-block w-1.5 h-1.5 rounded-full bg-amber-500 align-middle', className)}
        title={t('tickets:validation.pstOutTitle')}
        aria-label={t('tickets:validation.pstOutTitle')}
      />
    );
  };

  // One apartment row — extracted so it renders identically whether the table is flat (single address)
  // or split into per-address groups.
  const renderApartmentRow = (a: MduApartmentDto) => {
    const notDeployed = notInDeploymentList(a);
    return (
      <tr
        key={a.jvid}
        role="button"
        tabIndex={0}
        aria-label={t('tickets:validation.siblingDetailTitle', { jvid: a.jvid })}
        onClick={() => setSelectedSibling(a)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedSibling(a); }
        }}
        className={cn(
          'border-t border-slate-100 cursor-pointer hover:bg-brand-rose/[0.04] transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose/40',
          // D0309-1 (user, 20-10) : la ligne du ticket prend la couleur des autres — le rose cachait l'ambre
          // d'une unité inactive. Le badge « ce ticket » suffit à la repérer.
          inactiveApartment(a) && 'bg-amber-100',
        )}
      >
        <td className="px-3 py-2 font-medium text-slate-800">
          {a.jvid}
          {renderPstDot(a.pstFile, 'ml-1')}
          {a.isCurrent && (
            <span className="ml-2 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
              {t(multi ? 'tickets:validation.multi.rowCurrent' : 'tickets:validation.mduThisTicket')}
            </span>
          )}
          {/* N45 — une AUTRE adresse du ticket dans ce bâtiment : le badge ouvre son onglet. Il ne doit pas ouvrir la
              fiche du logement en plus — la ligne entière en est le bouton. */}
          {a.isTicketAddress && !a.isCurrent && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); selectTab(a.jvid); }}
              onKeyDown={(e) => e.stopPropagation()}
              title={t('tickets:validation.multi.rowBadgeTitle')}
              className="ml-2 inline-flex items-center gap-1 rounded bg-brand-rose/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand-rose hover:bg-brand-rose/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose/40"
            >
              <MapPinned className="h-3 w-3" aria-hidden="true" />
              {t('tickets:validation.multi.rowBadge')}
            </button>
          )}
          <AttentionBadge ruleIds={computeAttentionPoints(a.attributes)} className="ml-2" />
        </td>
        <td className="px-3 py-2 text-slate-600">
          {(() => {
            // N37 / D0309-6 — « adresse du DMP → adresse déclarée », avec la flèche et les couleurs de la boîte.
            const declared = declaredAddress(a);
            const twins = declaredDuplicates.get(a.jvid);
            return (
              <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {declared ? (
                  <span
                    className="inline-flex items-center gap-1"
                    title={t('tickets:validation.addressMappedTitle', { orig: mduAddress(a), declared: declared.full })}
                  >
                    <span className="text-slate-400">{mduAddress(a)}</span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-brand-rose" />
                    <span className="font-semibold text-brand-rose">{declared.short}</span>
                  </span>
                ) : mduAddress(a)}
                {/* N38 — même adresse vue par l'opérateur qu'un autre logement de la fenêtre : signalé sur les
                    DEUX lignes, tant que le doublon existe. Rien n'est bloqué. */}
                {twins && (
                  <span
                    className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700"
                    title={t('tickets:validation.declaredDup.rowTitle', { count: twins.length, jvids: twins.join(', ') })}
                  >
                    {t('tickets:validation.declaredDup.badge')}
                  </span>
                )}
              </span>
            );
          })()}
        </td>
        <td className="px-3 py-2 text-slate-600">
          {a.mappedBusNumber != null ? (
            <span
              className="inline-flex items-center gap-1"
              title={t('tickets:validation.busMappedTitle', { orig: a.busNumber ?? '—' })}
            >
              <span className="text-slate-400">{a.busNumber ?? '—'}</span>
              <ArrowRight className="h-3 w-3 text-brand-rose" />
              {/* Une boîte déclarée VIDE se dit : sans le tiret, la flèche ne pointerait sur rien. */}
              <span className="font-semibold text-brand-rose">{a.mappedBusNumber || '—'}</span>
            </span>
          ) : (
            a.busNumber || '—'
          )}
        </td>
        <td className="px-3 py-2">
          {a.attributes?.status === 'HOMES_CONNECT' ? (
            <span className="inline-flex items-center gap-1 font-semibold text-red-700">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-red-600 text-white text-[9px] font-bold leading-none">!</span>
              {a.attributes.status}
            </span>
          ) : (
            <span className="text-slate-600">{a.attributes?.status ?? '—'}</span>
          )}
          {/* 22-10 — l'UTAC d'un logement connecté, sous son statut : c'est lui qui identifie la connexion. */}
          {a.attributes?.status === 'HOMES_CONNECT' && a.attributes.utac && (
            <span className="block font-mono text-[10px] text-slate-500" title={t('tickets:validation.fields.utacHint')}>
              {t('tickets:validation.fields.utac')} {a.attributes.utac}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-slate-600">
          {a.attributes?.deliveryStatus != null ? (
            <span
              className={cn(
                'cursor-help underline decoration-dotted underline-offset-2',
                isBlockedDeliveryCode(Number(a.attributes.deliveryStatus))
                  ? 'font-semibold text-red-700 decoration-red-300'
                  : 'decoration-slate-300',
              )}
              title={formatDeliveryStatus(Number(a.attributes.deliveryStatus))}
            >
              {a.attributes.deliveryStatus}
            </span>
          ) : '—'}
        </td>
        <td className="px-3 py-2">
          {a.attributes?.scopelistV15 ? (
            <span className="text-slate-600">{a.attributes.scopelistV15}</span>
          ) : (
            <span className={cn('font-semibold', notDeployed ? 'text-amber-700' : 'text-slate-400')}>—</span>
          )}
        </td>
        <td className="px-3 py-2 text-slate-600">{a.attributes?.scopelistV2 ?? '—'}</td>
        {/* N42 — le caddie, avec le statut AFFICHÉ deux cellules plus haut. Une ligne synthétique (`added`) n'a
            pas de logement dans le DMP, donc pas d'ordre. Pas de clic propre : la ligne reste UN bouton. */}
        <td className="px-2 py-2 text-center">
          {!a.added && <OpenOrderCart state={openOrders} jvid={a.jvid} unitStatus={a.attributes?.status} />}
        </td>
      </tr>
    );
  };
  // N42 — la légende du caddie n'apparaît que s'il y en a un à l'écran, comme celle des lignes ambre.
  const anyOpenOrderCart = (mduApartments ?? []).some((a) => !a.added
    && unitShowsOpenOrderCart(a.attributes?.status, openOrdersFor(openOrders, a.jvid)));

  // ONE contact block (user ask 2026-07-30): a single message + the three outbound actions — the text rides with
  // WHICHEVER button is clicked. Public question to the operator; internal hand-offs (escalate / Build) with the
  // message joined as a note. N45 : le même bloc fait la carte « Le ticket entier » d'un ticket à plusieurs adresses —
  // ses envois partent pour toutes ses adresses, par les mêmes routes « niveau ticket ».
  const renderTicketContact = () => (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p id="av-no-address-contact" className="text-xs font-semibold text-slate-600 mb-2">{t('tickets:validation.noAddressContactTitle')}</p>
      {panels.ticket.operatorReplyAt ? (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-teal-100 bg-teal-50 px-3 py-2 text-xs text-teal-800" role="status">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
          <span>{t('tickets:validation.operatorRepliedNote', { date: new Date(panels.ticket.operatorReplyAt).toLocaleString() })}</span>
        </div>
      ) : panels.ticket.questionAskedAt ? (
        <p className="mb-2 text-[11px] text-slate-500">
          {t('tickets:validation.questionAskedNote', { date: new Date(panels.ticket.questionAskedAt).toLocaleString() })}
        </p>
      ) : null}
      {panels.ticket.escalatedAt && (
        <p className="mb-2 text-[11px] font-medium text-orange-700">
          {t('tickets:validation.noAddressEscalatedNote', { date: new Date(panels.ticket.escalatedAt).toLocaleString() })}
        </p>
      )}
      {panels.ticket.buildHandedOffAt && !panels.ticket.buildReplyAt && (
        <p className="mb-2 text-[11px] font-medium text-sky-700">
          {t('tickets:validation.noAddressBuildSentNote', { date: new Date(panels.ticket.buildHandedOffAt).toLocaleString() })}
        </p>
      )}
      {panels.ticket.buildReplyAt && (
        <p className="mb-2 text-[11px] font-medium text-teal-700">
          {t('tickets:validation.buildRepliedNote', { date: new Date(panels.ticket.buildReplyAt).toLocaleString() })}
        </p>
      )}
      {/* Réponses types (N1) : on APPEND, on n'écrase pas — le relecteur a souvent déjà tapé
          une phrase de contexte avant d'aller chercher le modèle. */}
      <SnippetInsert
        disabled={askingOperator || escalating || sendingToBuild || lockedByOther}
        currentText={noAddrMessage}
        onInsert={(txt) => setNoAddrMessage((prev) => (prev.trim() ? `${prev}\n${txt}` : txt))}
        onClear={() => { setNoAddrMessage(''); setNoAddrMentions([]); }}
      />
      <MentionTextarea
        value={noAddrMessage}
        onChange={setNoAddrMessage}
        picked={noAddrMentions}
        onPicked={setNoAddrMentions}
        mentionsEnabled={noAddrInternal}
        placeholder={t(noAddrInternal ? 'tickets:validation.noAddressContactPlaceholderInternal' : 'tickets:validation.noAddressContactPlaceholder')}
        rows={MESSAGE_ROWS}
        maxLength={5000}
        disabled={askingOperator || escalating || sendingToBuild || lockedByOther}
        className="mb-2"
      />
      <label className="mb-2 flex w-fit cursor-pointer items-center gap-2 text-xs font-semibold text-slate-700">
        <input
          type="checkbox"
          checked={noAddrInternal}
          onChange={(e) => setNoAddrInternal(e.target.checked)}
          disabled={askingOperator || escalating || sendingToBuild || lockedByOther}
          className="h-4 w-4 rounded border-slate-300 text-brand-rose focus:ring-brand-rose/30"
        />
        <Lock className="h-3.5 w-3.5 text-violet-600" />
        {t('tickets:validation.internalQuestionToggle')}
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={handleAskOperator}
          disabled={askingOperator || escalating || sendingToBuild || !noAddrMessage.trim() || lockedByOther}
        >
          {askingOperator ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MessageSquareText className="w-4 h-4 mr-1.5" />}
          {t('tickets:validation.askOperatorSend')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setConfirmOpen('escalate')}
          disabled={askingOperator || escalating || sendingToBuild || lockedByOther || !panels.escalationTarget
            || (!!panels.ticket.escalatedAt && panels.ticket.escalatedToId === panels.escalationTarget.userId)}
          title={panels.escalationTarget ? undefined : t('tickets:validation.escalateNoTarget')}
          className="border-orange-300 text-orange-700 hover:bg-orange-50"
        >
          {escalating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowUpRight className="w-4 h-4 mr-1.5" />}
          {panels.escalationTarget
            ? t('tickets:validation.escalate', { label: panels.escalationTarget.label })
            : t('tickets:validation.escalateNoTargetBtn')}
        </Button>
        {/* F4 (complété 07-08) — redescendre le ticket chez celui qui l'a escaladé, avec la
            réponse. N'apparaît que pour le destinataire d'une escalade dont l'émetteur est connu. */}
        {panels.returnTarget && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmOpen('return')}
            disabled={askingOperator || escalating || sendingToBuild || returning || lockedByOther}
            className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
          >
            {returning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Reply className="w-4 h-4 mr-1.5" />}
            {t('tickets:validation.returnBtn', { label: panels.returnTarget.label })}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => setConfirmOpen('build')}
          disabled={askingOperator || escalating || sendingToBuild || lockedByOther || !panels.buildTarget || (!!panels.ticket.buildHandedOffAt && !panels.ticket.buildReplyAt)}
          title={panels.buildTarget ? undefined : t('tickets:validation.sendToBuildNoTarget')}
          className="border-sky-300 text-sky-700 hover:bg-sky-50"
        >
          {sendingToBuild ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <HardHat className="w-4 h-4 mr-1.5" />}
          {t('tickets:validation.sendToBuild')}
        </Button>
      </div>
      {/* N29.1 — un ticket sans adresse crée aussi des adresses : le marqueur y a sa place. */}
      {id && (
        <div className="mt-2">
          <BuildRepliedMarker
            ticketId={id}
            buildRepliedAt={panels.ticket.buildRepliedAt}
            buildRepliedSource={panels.ticket.buildRepliedSource}
            canMark={isAdmin}
            disabled={lockedByOther}
            onMarked={markBuildRepliedLocally}
          />
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-400">{t('tickets:validation.noAddressContactHint')}</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Back + next + title */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-5">
          <Link to={`/tickets/${ticket.id}`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-rose">
            <ArrowLeft className="w-4 h-4" /> {t('tickets:validation.backToTicket')}
          </Link>
          {/* Previous / next ticket of the CURRENT view — walk the queue in both directions without
              returning to the list. Grouped tighter than the « back to ticket » link so the pair
              reads as pagination rather than three unrelated links. */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={goToPrevTicket}
              disabled={!prevTicketId}
              title={prevTicketId ? undefined : t('tickets:validation.prevTicketNone')}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-rose disabled:opacity-40 disabled:hover:text-slate-500 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" /> {t('tickets:validation.prevTicket')}
            </button>
            <span className="select-none text-slate-300" aria-hidden="true">·</span>
            <button
              type="button"
              onClick={goToNextTicket}
              disabled={!nextTicketId}
              title={nextTicketId ? undefined : t('tickets:validation.nextTicketNone')}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-rose disabled:opacity-40 disabled:hover:text-slate-500 disabled:cursor-not-allowed"
            >
              {t('tickets:validation.nextTicket')} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <AddressManagementMenu
          context={{
            streetName: reference?.streetName ?? mapping?.streetName,
            postalCode: reference?.postalCode ?? mapping?.postalCode,
            municipality: reference?.municipality ?? mapping?.municipality,
            city: reference?.city ?? mapping?.city,
            belmapBuildingId: reference?.belmapBuildingId,
            jvid: reference?.jvid ?? mapping?.jvid,
            ticketId: id,
            ticketNumber: ticket.ticketNumber,
            // N29.1 : Build a répondu sur ce ticket → les formulaires posent « Build a-t-il confirmé cette adresse ? ».
            buildRepliedAt: ticket.buildRepliedAt ?? null,
            // N29.4 : « Non » ne crée rien ; la page prépare le refus (verdict, ou message sans JVID).
            onBuildDeclined: prepareBuildRefusal,
            buildDeclineMode: panels.noAddress ? 'message' : 'verdict',
            dwellingUnitType: (reference?.dwellingUnitType ?? mapping?.dwellingUnitType ?? null) as 'SDU' | 'MDU' | null,
            // Les appartements bruts, pas des unités déjà converties : le formulaire d'ajout de boîte
            // doit pouvoir les FILTRER sur l'adresse choisie (par jvid), sinon il avertirait sur la
            // capacité du bâtiment entier alors qu'il crée sur une seule adresse.
            capacityApartments: mduApartments ?? undefined,
            // …et la MÊME lecture COCON que les pastilles (C0.1).
            capacityCocon: facadeCocon,
          }}
          // Refresh the reference + "Other apartments" list after a create/add-box so a newly-added box
          // shows immediately (keeps the reviewer's in-progress edits — see mergeRefreshedPanels).
          onMutated={handleRefresh}
        />
      </div>
      <h1 className="text-xl font-bold gradient-text">{t('tickets:validation.pageTitle')}</h1>

      {/* Anti-collision: someone else is working this ticket — reading is free, actions are off. */}
      {lockedByOther && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-wrap items-center gap-3" role="alert">
          <Lock className="w-5 h-5 text-red-600 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-red-700">
              {t('tickets:validation.lock.banner', { name: lock?.holder?.name ?? '—' })}
            </p>
            <p className="text-xs text-red-600">
              {lock?.holder?.since
                ? t('tickets:validation.lock.since', {
                    time: new Date(lock.holder.since).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  })
                : t('tickets:validation.lock.readOnlyHint')}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={tryAcquireLock}
              className="border-red-300 text-red-700 hover:bg-red-100">
              {t('tickets:validation.lock.retry')}
            </Button>
            {isAdmin && (
              <Button type="button" variant="outline" size="sm" onClick={handleForceTakeover}
                className="border-red-300 text-red-700 hover:bg-red-100">
                {t('tickets:validation.lock.force')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Ticket context strip. z-10: the glass-card backdrop-filter makes each card its own stacking
          context, so the reclassify dropdown's z-index only counts INSIDE this card — without lifting
          the whole strip, the next card (Ticket description) paints over the open menu. Kept BELOW the
          top bar's Manage-address dropdown (z-40), which the first z-30 attempt was hiding. */}
      <GlassCard className="relative z-10 p-4 flex flex-wrap items-center gap-x-8 gap-y-2">
        <ContextItem k={t('tickets:validation.ticketContext')}>
          {ticket.url ? (
            // External deep link to the ORIGINAL ticket in Uniticket (the in-app /tickets/:id route
            // does not exist in standalone AV — it used to fall through to the queue redirect).
            <a
              href={ticket.url}
              target="_blank"
              rel="noopener noreferrer"
              title={t('tickets:validation.openInUniticket')}
              className="font-bold gradient-text hover:opacity-75 transition-opacity"
            >
              {ticket.ticketNumber}
            </a>
          ) : (
            <span className="font-bold gradient-text">{ticket.ticketNumber}</span>
          )}
          {/* Ouvrir ce JVID dans la console DMP (demande user 2026-09-02, ADMINS SEULS).
              Pourquoi la PAGE et pas le tiroir déjà ouvert par un clic sur un JVID : le tiroir est en
              lecture seule, la page porte les actions d'écriture (créer, ajouter une boîte, modifier,
              échanger, poser un fake ODF). C'est pour agir qu'on y va, pas pour regarder.
              Le jvid ET le dossier voyagent dans l'URL : le lien reste partageable, il survit à un
              rechargement, et les flèches du navigateur fonctionnent.
              ⚠️ MÊME ONGLET, choix explicite du user : la fenêtre se démonte, donc ce qui est saisi et
              non enregistré est perdu. Il l'a accepté en connaissance de cause.
              Réduit au seul logo, aux couleurs Unifiber (2026-09-02) : il reprend la pastille du titre
              de la console en miniature, donc on reconnaît la destination sans la lire. `aria-label`
              porte ce que le texte disait — un bouton réduit à une icône reste un bouton à nommer. */}
          {isAdmin && !panels.noAddress && (reference?.jvid ?? mapping?.jvid) != null && (
            <Link
              to={`/dmp-console?jvid=${reference?.jvid ?? mapping?.jvid}&from=${id}`
                + (ticket.ticketNumber ? `&fromNo=${encodeURIComponent(ticket.ticketNumber)}` : '')}
              title={t('tickets:validation.openInDmpConsole')}
              aria-label={t('tickets:validation.openInDmpConsole')}
              className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-md bg-brand-gradient align-middle text-white shadow-sm transition-opacity hover:opacity-80"
            >
              <Database className="h-3 w-3" />
            </Link>
          )}
        </ContextItem>
        <ContextItem k={t('tickets:detail.status', 'Status')}><StatusBadge status={ticket.status} /></ContextItem>
        <ContextItem k={t('tickets:validation.createdAt', 'Created')}><span className="text-sm font-semibold">{formatDate(ticket.createdAt)}</span></ContextItem>
        <ContextItem k={t('tickets:validation.operator', 'Operator')}><span className="text-sm font-semibold">{ticket.operatorName}</span></ContextItem>
        <ContextItem k={t('tickets:validation.contact', 'Requester')}><span className="text-sm font-semibold">{ticket.contactName ?? '—'}</span></ContextItem>
        <div className="ml-auto flex items-center gap-3">
          {/* N45 : remonté à chaque onglet — l'enrichissement porte sur l'adresse ouverte. */}
          {id && !panels.noAddress && (reference?.jvid ?? mapping?.jvid) != null && (
            <AddressEnrichmentPanel key={panels.activeJvid ?? 'primary'} ticketId={id} jvid={activeJvidParam} />
          )}
          <ValidationPill status={form.status} />
          {/* Discreet manual queue move (local only) — for tickets handled outside the app. Hidden on
              verdicted/escalated rows: those states must move through the real actions. N45 : un ticket à
              plusieurs adresses se range comme un ticket sans adresse — en entier. */}
          {id && (ticketLevel || panels.validation?.status === 'PENDING' || panels.validation?.status === 'WAITING_BUILD') && (
            <TicketReclassifyMenu
              ticketId={id}
              ticketNumber={panels.ticket.ticketNumber}
              buildParked={ticketLevel
                ? !!panels.ticket.buildHandedOffAt && !panels.ticket.buildReplyAt
                : panels.validation?.status === 'WAITING_BUILD'}
              questionOpen={ticketLevel
                ? !!panels.ticket.questionAskedAt && !panels.ticket.operatorReplyAt
                : !!panels.validation?.questionAskedAt && !panels.validation?.operatorReplyAt}
              disabled={lockedByOther}
              onDone={load}
            />
          )}
        </div>
      </GlassCard>

      {/* Honesty banners */}
      {panels.triggeredByOtherTicket && (
        <Banner tone="warn" icon={<AlertTriangle className="w-4 h-4" />}>
          {t('tickets:validation.bannerOtherTicket')}
        </Banner>
      )}
      {panels.isStale && (
        <Banner tone="info" icon={<Info className="w-4 h-4" />}>
          {t('tickets:validation.bannerStale')}
        </Banner>
      )}

      {/* Uniticket escalation — a complaint: flagged loud, handled first (user ask 2026-07-29). */}
      {panels.ticket.escalated && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-2.5" role="alert">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
          <div className="text-sm text-red-800">
            <p className="font-bold">{t('tickets:validation.escalatedBanner')}</p>
            {panels.ticket.escalationReason && (
              <p className="mt-1 whitespace-pre-line text-xs">{panels.ticket.escalationReason}</p>
            )}
          </div>
        </div>
      )}

      {/* Duplicate-request radar (user ask 2026-07-30, case 691/793): other AM tickets anchored
          anywhere in the same building — the reviewer sees them BEFORE handling this one. */}
      {(panels.buildingTickets?.length ?? 0) > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-2.5" role="alert">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <div className="min-w-0 text-sm text-amber-800">
            <p className="font-bold">
              {t('tickets:validation.buildingTickets.title', { count: panels.buildingTickets!.length })}
            </p>
            <ul className="mt-1 space-y-0.5 text-xs">
              {panels.buildingTickets!.map((bt) => (
                <li key={bt.id} className="truncate">
                  <Link
                    to={`/tickets/${bt.id}/address-validation`}
                    className="font-semibold underline decoration-amber-400 underline-offset-2 hover:text-amber-950"
                  >
                    {bt.ticketNumber}
                  </Link>
                  {' — '}
                  {['RESOLVED', 'CLOSED'].includes(bt.status)
                    ? t('tickets:validation.buildingTickets.resolvedOn', { date: formatDate(bt.updatedAt) })
                    : t('tickets:validation.buildingTickets.openSince', { date: formatDate(bt.createdAt) })}
                  {bt.jvid != null && <span className="text-amber-700/70"> · JVID <JvidLink jvid={bt.jvid} /></span>}
                  {bt.subject?.trim() && <span className="text-amber-700/80"> · {bt.subject}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Ticket description — headed by the Uniticket SUBJECT (user ask 2026-07-31: the title often
          carries THE address, e.g. « Place des Déportés 1490 boites », and was shown nowhere in AV). */}
      <GlassCard className="p-4">
        <h2 className="text-sm font-bold text-slate-800 mb-1">{t('tickets:validation.description')}</h2>
        {ticket.subject?.trim() && (
          <p className="text-sm font-semibold text-slate-800 mb-1">{ticket.subject}</p>
        )}
        <p className="text-sm text-slate-600 whitespace-pre-line">
          {ticket.description?.trim() || <span className="text-slate-400">{t('tickets:validation.noDescription')}</span>}
        </p>
      </GlassCard>

      {/* Operator attachments from Uniticket (files/images on the ticket) — read-only, hidden when none */}
      {id && <TicketAttachments key={attachmentsVersion} ticketId={id} />}

      {/* N45 — les adresses du ticket : un onglet chacune (à partir de deux), et « Ajouter une adresse ». Pas en mode
          sans adresse : il faut d'abord une adresse principale, créée ou liée. */}
      {id && !panels.noAddress && (
        <TicketAddressBar
          ticketId={id}
          addresses={panels.addresses ?? []}
          activeJvid={panels.activeJvid ?? null}
          multi={multi}
          disabled={lockedByOther}
          busy={switching}
          onSelect={selectTab}
          onAdded={(_jvid, data) => showPanels(data)}
          onRemoved={(jvid, data) => {
            // L'onglet ouvert est retiré : la réponse est la fenêtre sur l'adresse principale. Un autre l'est : seuls
            // les onglets changent, le formulaire en cours reste.
            if (jvid === panels.activeJvid) showPanels(data);
            else setPanels((prev) => (prev ? { ...prev, addresses: data.addresses, multiAddress: data.multiAddress } : prev));
          }}
        />
      )}

      {/* N45 — pendant la lecture d'un autre onglet, l'adresse quittée reste à l'écran mais inerte : un geste fait là
          viserait une adresse qui n'est déjà plus celle de l'URL. */}
      <div className={cn('space-y-4', switching && 'pointer-events-none select-none opacity-60')} inert={switching} aria-busy={switching || undefined}>
      {/* Panels 1 + 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Panel 1 — reference */}
        <GlassCard className="p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              {/* flex-wrap (02-11) : les pastilles passent à la ligne plutôt que de filer sous la colonne de droite,
                  qui ne rétrécit pas — sur un SDU pas encore releasé, elle recouvrait le bonhomme Street View. */}
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-bold text-slate-800">{t('tickets:validation.panel1Title')}</h2>
                <AttentionBadge ruleIds={computeAttentionPoints(referenceAttributes)} />
                {renderPstDot(panels.pstFile)}
                {/* SSV / TSA d'un SDU : il n'a pas de tableau MDU, donc pas de sous-en-tête où la poser à côté de
                    Capacité. En MDU, elle vit dans le sous-en-tête de chaque adresse. */}
                {!mduApartments?.length && reference?.jvid != null && (
                  <SsvTsaPill
                    state={ssvTsa}
                    jvids={[reference.jvid]}
                    onOpen={(docs) => setSsvTsaOpen({
                      label: addressLine(reference.streetName ?? null, reference.houseNumber ?? null, reference.houseNumberExtension ?? null),
                      docs,
                    })}
                  />
                )}
                {/* Service file / Deployment list d'un SDU (22-10), juste après SSV / TSA, pour la même raison. */}
                {!mduApartments?.length && reference?.jvid != null && (
                  <BuildFilePills
                    state={buildFiles}
                    units={[{
                      jvid: reference.jvid,
                      label: addressLine(reference.streetName ?? null, reference.houseNumber ?? null, reference.houseNumberExtension ?? null),
                    }]}
                    onOpen={(kind, hits) => setBuildFileOpen({ kind, label: hits[0]?.label ?? '', hits })}
                  />
                )}
                {/* N42 — le caddie d'un SDU, au même endroit et pour la même raison : pas de tableau où le poser. */}
                {!mduApartments?.length && reference?.jvid != null && (
                  <OpenOrderCart state={openOrders} jvid={reference.jvid} unitStatus={referenceAttributes?.status} />
                )}
                {/* Street View (2026-10-16) : la façade de l'adresse du ticket sans quitter la page, en SDU comme en
                    MDU — les logements d'un MDU partagent le point de leur bâtiment. Grisé sans coordonnées. */}
                <StreetViewButton
                  point={addressPoint(reference)}
                  label={reference
                    ? [
                        addressLine(reference.streetName ?? null, reference.houseNumber ?? null, reference.houseNumberExtension ?? null),
                        [reference.postalCode, reference.city || reference.municipality].filter(Boolean).join(' '),
                      ].filter(Boolean).join(' · ')
                    : null}
                />
              </div>
              {!mduApartments?.length && reference?.jvid != null && <OpenOrdersNotice state={openOrders} />}
              <p className="text-xs text-slate-400">
                {panels.noAddress
                  ? t('tickets:validation.panel1SubNoAddress')
                  : t('tickets:validation.panel1Sub', { jvid: reference?.jvid ?? mapping?.jvid ?? '—' })}
              </p>
            </div>
            <div className="text-right shrink-0">
              <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                {t('tickets:validation.refreshDmp')}
              </Button>
              {/* N31 (décision du 28-09) — défaire une liaison faite par erreur, motif obligatoire ; ouvert aux
                  validateurs depuis N31.1 (30-09), comme « Lier ». Discret à dessein, sous le bouton de
                  rafraîchissement : c'est une réparation, pas un geste du parcours. Le serveur refuse tout ce qui ne
                  se défait pas en remettant la liaison ; la fenêtre dit pourquoi. Grisé quand un collègue tient le
                  dossier : le geste passe par le verrou. N45 : sur l'onglet de l'adresse principale seulement — une
                  adresse AJOUTÉE se retire par la croix de son onglet. */}
              {!panels.noAddress && id && onPrimaryTab && (
                <>
                  <button
                    type="button"
                    onClick={() => setUnlinkOpen(true)}
                    disabled={lockedByOther}
                    title={t('tickets:validation.unlink.btnHint')}
                    className="mt-1.5 flex w-full items-center justify-end gap-1 text-[11px] font-medium text-rose-700 underline decoration-dotted underline-offset-2 hover:text-rose-900 disabled:opacity-50"
                  >
                    <Unlink className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {t('tickets:validation.unlink.btn')}
                  </button>
                  <UnlinkTicketModal
                    ticketId={id}
                    open={unlinkOpen}
                    onClose={() => setUnlinkOpen(false)}
                    // N31.1 : la déliaison se lit désormais dans l'historique du ticket — on le relit aussi.
                    onUnlinked={() => { void load(); void loadHistory(); }}
                  />
                </>
              )}
              {/* SDU only: the ticket's own address has no "Other apartments" row to click, so give it a
                  direct entry into the same release/un-release sheet (no-DELETE rule: a "delete this
                  address" request is handled by un-releasing). MDU keeps the isCurrent row as sole entry.
                  Disabled while the live DMP attributes are missing — without them the panel could show
                  the Release form for an already-released address. */}
              {!mduApartments?.length && !panels.noAddress && reference?.jvid != null && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'mt-2 h-7 text-xs block w-full',
                      // Not released yet → AMBER, like the MDU sheet's own signalling (user ask
                      // 2026-08-06: « tant qu'il n'est pas release, le bouton est en jaune » — the step
                      // was being forgotten after a create). Back to the plain outline once released.
                      referenceAttributes && !sduReleased
                        && 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100',
                    )}
                    disabled={!referenceAttributes}
                    title={!referenceAttributes ? t('tickets:validation.currentReleaseUnavailable') : undefined}
                    onClick={() => setSelectedSibling(currentAsApartment(panels))}
                  >
                    {t(sduReleased ? 'tickets:validation.currentManageBtn' : 'tickets:validation.currentReleaseBtn')}
                  </Button>
                  {referenceAttributes && !sduReleased && (
                    <p className="mt-1 text-[10px] font-medium text-amber-700 text-left">
                      {t('tickets:validation.notReleasedHint')}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          <AddressReferenceView reference={reference} attributes={referenceAttributes} buildFiles={panels.buildFiles} />
        </GlassCard>

        {/* Panel 2 — mapping (editable); replaced by the no-address helper when the ticket has no jvid:
            Panel 1 then shows the OPERATOR'S REQUESTED address, and the reviewer either creates it
            (Manage-address menu, pre-filled from that reference) or links an existing JVID here. */}
        {canLink ? (
          <GlassCard className="p-4 border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-bold text-slate-800">
                {panels.noAddress
                  ? t('tickets:validation.noAddressTitle')
                  : t('tickets:validation.emptyRef.title')}
              </h2>
            </div>
            {/* Deux situations, deux explications : pas de jvid du tout, ou un jvid qui ne désigne plus
                rien. Le relecteur ne doit plus avoir à deviner devant une fiche de tirets. */}
            <p className="text-xs text-slate-500 mb-4 whitespace-pre-line">
              {panels.noAddress
                ? t('tickets:validation.noAddressHint')
                : t('tickets:validation.emptyRef.hintMissing', { jvid: relinkFrom ?? '—' })}
            </p>
            {/* Duplicate guard (user case 2026-07-30): existing DMP rows matching the requested address —
                link one instead of creating a twin (the frequent reality: it exists, just not released). */}
            {(panels.addressCandidates?.length ?? 0) > 0 && (
              <div className="mb-4">
                <p className="flex items-center gap-1.5 text-xs font-bold text-red-700 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {t(panels.noAddress
                    ? 'tickets:validation.candidatesTitle'
                    // N28 : la même liste, mais pas le même propos — ici elle ne garde pas d'un doublon,
                    // elle propose l'adresse qui REMPLACE celle que le DMP ne sert plus.
                    : 'tickets:validation.candidatesTitleRelink', { count: panels.addressCandidates!.length })}
                </p>
                {panels.addressCandidates!.map((c) => (
                  <div key={c.jvid} className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50/50 p-2.5 mb-2">
                    <div className="text-xs">
                      <p className="font-semibold text-slate-800">
                        {c.jvid} · {c.streetName} {c.houseNumberExtension || c.houseNumber}
                        {c.busNumber ? ` · bus ${c.busNumber}` : ''} · {c.postalCode} {c.city ?? ''}
                      </p>
                      <p className="text-slate-600">
                        {c.status ?? '—'}{c.dwellingUnitType ? ` · ${c.dwellingUnitType}` : ''} ·{' '}
                        {c.homeReleased
                          ? t('tickets:validation.candidateReleased')
                          : <span className="font-semibold text-amber-700">{t('tickets:validation.candidateNotReleased')}</span>}
                        {c.homeReleaseBlockedReason && (
                          <span className="font-semibold text-red-700">
                            {' · '}{t('tickets:validation.candidateBlocked', { reason: c.homeReleaseBlockedReason, date: c.homeReleaseBlockedAt ?? '—' })}
                          </span>
                        )}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" className="shrink-0" onClick={() => setLinkJvid(String(c.jvid))}>
                      {t('tickets:validation.candidateUse')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">{t('tickets:validation.noAddressLinkLabel')}</p>
              <div className="flex gap-2">
                <Input
                  value={linkJvid}
                  onChange={(e) => setLinkJvid(e.target.value.replace(/\D/g, ''))}
                  placeholder="JVID"
                  className="max-w-[160px]"
                  aria-label="JVID"
                />
                {/* N28 : remplacer l'adresse d'un ticket EN COURS passe par une confirmation qui montre
                    ancien → nouveau. Relier un ticket qui n'avait rien reste direct : il n'y a rien à
                    remplacer, et le geste d'origine n'avait pas de confirmation. */}
                <Button
                  onClick={() => (relinkFrom != null ? setRelinkConfirm(true) : void handleLinkAddress())}
                  disabled={linking || !linkJvid || lockedByOther}
                >
                  {linking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                  {relinkFrom != null
                    ? t('tickets:validation.relink.btn')
                    : t('tickets:validation.noAddressLinkBtn')}
                </Button>
              </div>
            </div>
            {!multi && renderTicketContact()}
          </GlassCard>
        ) : (
        <GlassCard className="p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-sm font-bold text-slate-800">{t('tickets:validation.panel2Title')}</h2>
              <p className="text-xs text-slate-400">{t('tickets:validation.panel2Sub')}</p>
            </div>
            {mapping?.modifiedBy && (
              <span className="text-[10px] text-slate-400 shrink-0">{t('tickets:validation.modifiedBy', { who: mapping.modifiedBy })}</span>
            )}
          </div>
          {(mapping || reference) ? (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{mapping ? 'JVID · operator' : 'JVID'}</span>
                <span className="font-medium text-slate-700">
                  {mapping ? `${mapping.jvid} · ${mapping.operatorId}` : (reference?.jvid ?? '—')}
                  <span className="ml-2 text-[10px] uppercase bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">
                    {mapping ? t('tickets:validation.key') : t('tickets:validation.newMapping')}
                  </span>
                </span>
              </div>
              {/* No saved mapping row yet → the form is pre-filled from the creator's submitted address
                  (the reference); saving creates the address_mapping_table row. */}
              {!mapping && (
                <p className="text-[11px] text-sky-800 bg-sky-50 border border-sky-100 rounded-lg px-2.5 py-1.5">
                  {t('tickets:validation.prefilledFromCreator')}
                </p>
              )}
              {EDIT_FIELDS.map((f) => {
                const diff = isDiff(f.key);
                const incoherent = (houseExtIncoherent && (f.key === 'houseNumber' || f.key === 'houseNumberExtension'))
                  || (mappingHouseNumNotDigits && f.key === 'houseNumber');
                return (
                  <div key={f.key} className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <label className="text-xs text-slate-500">{t(`tickets:validation.${f.labelKey}`)}</label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={form.mapping[f.key]}
                        onChange={(e) => setForm({ ...form, mapping: { ...form.mapping, [f.key]: e.target.value } })}
                        className={cn('h-8 text-sm',
                          incoherent ? 'bg-rose-50 border-rose-300 focus-visible:ring-rose-200'
                            : diff && 'bg-amber-50 border-amber-300 focus-visible:ring-amber-200')}
                        disabled={saving || lockedByOther}
                      />
                      {diff && (
                        <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                          {t('tickets:validation.diffTag')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Postal-code check (user ask 2026-08-28): shown ONLY while the code is being CHANGED —
                  active is the diff against the loaded baseline, so opening a ticket never fires it. */}
              <PostalCodeLocalityHint
                postalCode={form.mapping.postalCode}
                city={form.mapping.city}
                active={!!mappingBaseline && form.mapping.postalCode !== mappingBaseline.postalCode}
                disabled={saving || lockedByOther}
                onPick={(city, municipality) =>
                  setForm((prev) => (prev ? { ...prev, mapping: { ...prev.mapping, city, municipality } } : prev))}
              />
              <p className="text-[11px] text-slate-400 pt-1">{t('tickets:validation.fieldsHint')}</p>
              {mappingHouseNumNotDigits && (
                <p className="text-[11px] font-medium text-rose-600" role="alert">{t('tickets:addressMgmt.create.errHouseNumberDigits')}</p>
              )}
              {houseExtIncoherent && (
                <p className="text-[11px] font-medium text-rose-600" role="alert">{t('tickets:validation.houseExtIncoherent')}</p>
              )}
              {/* N38 — la saisie donne à ce logement l'adresse d'un autre : on le dit, on ne bloque pas. */}
              {mappingTwins.length > 0 && (
                <p className="text-[11px] font-medium text-rose-600" role="alert">
                  {t('tickets:validation.declaredDup.formWarning', { count: mappingTwins.length, jvids: mappingTwins.join(', ') })}
                </p>
              )}
              {/* « Valider » (F6): appears as soon as a text field is edited and writes the mapping row
                  on its own, so a correction is confirmed on the spot instead of silently riding on the
                  ticket verdict. Reverting every field back hides it again — nothing to write. */}
              {mappingDirty ? (
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveMappingOnly}
                    disabled={savingMapping || saving || lockedByOther || houseExtIncoherent || mappingHouseNumNotDigits}
                    className="bg-brand-gradient text-white hover:opacity-90"
                  >
                    {savingMapping
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Save className="w-3.5 h-3.5 mr-1.5" />}
                    {savingMapping ? t('tickets:validation.mappingSaving') : t('tickets:validation.mappingSaveBtn')}
                  </Button>
                  <span className="text-[11px] text-slate-400">{t('tickets:validation.mappingSaveHint')}</span>
                </div>
              ) : mappingSaved ? (
                <p className="flex items-center gap-1.5 pt-1 text-[11px] font-semibold text-emerald-700">
                  <Check className="w-3.5 h-3.5" />{t('tickets:validation.mappingSavedHint')}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-6 text-center">{t('tickets:validation.noMapping')}</p>
          )}
        </GlassCard>
        )}
      </div>

      {/* MDU siblings */}
      {mduApartments && mduApartments.length > 0 && (
        <GlassCard className="p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-brand-rose" />
              <h2 className="text-sm font-bold text-slate-800">{t('tickets:validation.mduTitle')}</h2>
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                {t('tickets:validation.mduCount', { count: mduApartments.length })}
              </span>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              {t('tickets:validation.refreshDmp')}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mb-2">{t('tickets:validation.mduRowHint')}</p>
          {mduAddressGroups.length > 1 && (
            <p className="text-[11px] text-amber-700 mb-2 flex items-center gap-1.5 font-medium">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              {t('tickets:validation.mduMultiAddress', { count: mduAddressGroups.length })}
            </p>
          )}
          {mduApartments.some(inactiveApartment) && (
            <p className="text-[11px] text-amber-700 mb-2 flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-300" />
              {t('tickets:validation.mduNotDeployedLegend')}
            </p>
          )}
          {anyOpenOrderCart && (
            <p className="text-[11px] text-red-700 mb-2 flex items-center gap-1.5">
              <ShoppingCart className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {t('tickets:validation.openOrders.legend')}
            </p>
          )}
          <OpenOrdersNotice state={openOrders} className="mb-2" />
          {/* N41 — ce que sont les lignes ambre glissées sous une adresse, et ce que leur lecture a à dire
              d'elle-même (échec : « impossible de vérifier », jamais un silence ; ou recherche bornée). */}
          <SameAddressBanner count={strangers.length - looseStrangers.length} className="mb-2" />
          <SameAddressNotice state={sameAddress} className="mb-2" />
          <div className="overflow-hidden rounded-xl border border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colJvid')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colAddress')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colBus')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colStatus')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colDeliveryStatus')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colDeploymentList')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colBuildList')}</th>
                  <th
                    className="w-8 px-2 py-2 text-center"
                    title={t('tickets:validation.openOrders.colLabel')}
                    aria-label={t('tickets:validation.openOrders.colLabel')}
                  >
                    <ShoppingCart className="inline-block h-3.5 w-3.5" aria-hidden="true" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Un sous-en-tête par adresse, TOUJOURS — même quand le bâtiment n'en a qu'une (choix
                    user 2026-09-04). C'est lui qui porte la capacité, et la capacité se joue par
                    adresse : la masquer dans le cas à une seule adresse reviendrait à ne pas dire de
                    QUOI le chiffre parle. */}
                {mduAddressGroups.map((g, gi) => (
                  <Fragment key={g.label}>
                    {/* Pas de bord haut sur le PREMIER groupe : il viendrait doubler celui du thead, et
                        sur un bâtiment mono-adresse les deux bandes grises se collent. */}
                    <tr className={cn('bg-slate-100/80', gi > 0 && 'border-t border-slate-200')}>
                      <td colSpan={APARTMENT_TABLE_COLS} className="px-3 py-1.5 text-xs font-semibold text-slate-600">
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          <span>
                            {g.label}
                            <span className="ml-1.5 font-normal text-slate-400">
                              · {t('tickets:validation.mduGroupUnits', { count: g.rows.length })}
                            </span>
                          </span>
                          <CapacityPill apartments={g.rows} cocon={facadeCocon} onOpen={() => setCapacityGroup(g)} />
                          <SsvTsaPill
                            state={ssvTsa}
                            jvids={g.rows.filter((a) => !a.added).map((a) => a.jvid)}
                            onOpen={(docs) => setSsvTsaOpen({ label: g.label, docs })}
                          />
                          {/* 22-10 — « Service file 3/5 » : les logements de CETTE adresse présents dans le fichier. */}
                          <BuildFilePills
                            state={buildFiles}
                            units={g.rows.filter((a) => !a.added).map((a) => ({
                              jvid: a.jvid,
                              label: a.mappedBusNumber ?? a.busNumber ?? String(a.jvid),
                            }))}
                            onOpen={(kind, hits) => setBuildFileOpen({ kind, label: g.label, hits })}
                          />
                        </span>
                      </td>
                    </tr>
                    {g.rows.map(renderApartmentRow)}
                    {/* N41 — sous CETTE adresse, ses logements rangés sous un autre building ID. Hors de
                        `g.rows` : la pastille de capacité ci-dessus ne les compte pas. */}
                    <SameAddressRows units={strangersOf(g.rows)} duplicates={declaredDuplicates} orders={strangerOrders} />
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}

      {/* N41 — sans tableau des logements (un logement seul dans son bâtiment), ou pour un logement « d'ailleurs »
          dont l'adresse n'est pas dans le tableau : la même liste, dans une carte à elle. C'est le cas qui a fait
          naître la demande — une boîte créée à côté de son bâtiment laisse l'adresse en « SDU » à l'écran. Quand
          le tableau existe, il a déjà dit ce que la lecture avait à dire : la carte ne le répète pas. */}
      <SameAddressCard
        units={looseStrangers}
        duplicates={declaredDuplicates}
        orders={strangerOrders}
        state={mduApartments && mduApartments.length > 0 ? { ...sameAddress, status: 'ready', truncated: false } : sameAddress}
      />

      {/* Panel 3 — validation */}
      {/* Ticket discussion thread — read-only, ABOVE the verdict so prior exchanges inform the decision. */}
      {id && <TicketThread ticketId={id} refreshKey={threadVersion} />}

      {/* No verdict panel in no-address mode: the verdict is keyed on (jvid, operator) — create or link
          the address first, the ticket then re-opens as a normal validation. */}
      {!panels.noAddress && (
      <GlassCard className="p-4">
        <h2 id="av-verdict" className="text-sm font-bold text-slate-800">{t('tickets:validation.panel3Title')}</h2>
        <p className="text-xs text-slate-400 mb-3">
          {multi
            ? t('tickets:validation.multi.verdictSub', { label: activeAddress ? ticketAddressLabel(activeAddress) : '—' })
            : t('tickets:validation.panel3Sub')}
        </p>
        <div className="flex gap-2 mb-3 max-w-md">
          {(['PENDING', 'VALIDATED', 'REJECTED'] as AddressValidationStatus[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setForm({ ...form, status: s })}
              // N45 — tant que le ticket attend un envoi, aucune adresse ne prend de verdict (décision ③).
              disabled={waitingFor != null && s !== 'PENDING'}
              className={cn(
                'flex-1 text-sm font-semibold py-2 rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                form.status === s ? statusActive(s) : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300',
              )}
            >
              {t(`tickets:validation.status${s.charAt(0) + s.slice(1).toLowerCase()}`)}
            </button>
          ))}
        </div>
        {waitingFor && (
          <div className="flex items-start gap-2 mb-3 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2" role="status">
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-sky-600" />
            <span>{t(`tickets:validation.multi.waiting.${waitingFor}`)}</span>
          </div>
        )}
        {/* WAITING_BUILD reminder: the ticket is parked awaiting a Build opinion, but closure stays free —
            pick a verdict once Build has replied (read the reply in Uniticket for now, #6 will surface it). */}
        {form.status === 'WAITING_BUILD' && (
          <div className="flex items-start gap-2 mb-3 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2">
            <HardHat className="w-4 h-4 shrink-0 mt-0.5 text-sky-600" />
            <span>{t('tickets:validation.waitingBuildNote')}</span>
          </div>
        )}
        {/* ONE message field (user rule 2026-07-30): whatever is written ALWAYS reaches Uniticket —
            public operator question on a PENDING save (round-trip badge on reply), public comment on
            a verdict, joined to the hand-offs (escalate public / Build internal note). The old
            internal-only « Remarque » no longer exists. */}
        {form.status === 'PENDING' && (
          panels.validation.operatorReplyAt ? (
            <div className="flex items-start gap-2 mb-2 text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2" role="status">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-teal-600" />
              <span>{t('tickets:validation.operatorRepliedNote', { date: new Date(panels.validation.operatorReplyAt).toLocaleString() })}</span>
            </div>
          ) : panels.validation.questionAskedAt ? (
            <p className="text-[11px] text-slate-500 mb-2">
              {t('tickets:validation.questionAskedNote', { date: new Date(panels.validation.questionAskedAt).toLocaleString() })}
            </p>
          ) : null
        )}
        <label className="text-xs text-slate-500">
          {/* N45 — sur un ticket à plusieurs adresses, rien ne part d'une adresse : le texte attend le récapitulatif. */}
          {t(multi ? 'tickets:validation.multi.noteLabel' : 'tickets:validation.messageLabel')}
          {form.status === 'REJECTED' && <span className="text-rose-600 ml-0.5">*</span>}
        </label>
        {/* Réponses types (N1), même règle qu'au-dessus : on ajoute à la suite du texte existant. */}
        <SnippetInsert
          disabled={saving}
          currentText={form.remark}
          onInsert={(txt) => setForm({ ...form, remark: form.remark.trim() ? `${form.remark}\n${txt}` : txt })}
          onClear={() => { setForm({ ...form, remark: '' }); setMsgMentions([]); }}
        />
        <MentionTextarea
          value={form.remark}
          onChange={(v) => setForm({ ...form, remark: v })}
          picked={msgMentions}
          onPicked={setMsgMentions}
          mentionsEnabled={!multi && form.status === 'PENDING' && msgInternal}
          rows={MESSAGE_ROWS}
          maxLength={5000}
          placeholder={t(multi ? 'tickets:validation.multi.notePlaceholder' : 'tickets:validation.messagePlaceholder')}
          disabled={saving}
          className={cn('mt-1', rejectNeedsRemark && 'border-rose-300 focus-visible:ring-rose-200')}
        />
        {!multi && form.status === 'PENDING' && (
          <label className="mt-1.5 flex w-fit cursor-pointer items-center gap-2 text-xs font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={msgInternal}
              onChange={(e) => setMsgInternal(e.target.checked)}
              disabled={saving}
              className="h-4 w-4 rounded border-slate-300 text-brand-rose focus:ring-brand-rose/30"
            />
            <Lock className="h-3.5 w-3.5 text-violet-600" />
            {t('tickets:validation.internalQuestionToggle')}
          </label>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          {multi ? t('tickets:validation.multi.noteHint') : (
            <>
              {form.status === 'PENDING'
                ? t(msgInternal ? 'tickets:validation.messageHintPendingInternal' : 'tickets:validation.messageHintPending')
                : form.status === 'VALIDATED' ? t('tickets:validation.messageHintValidated')
                : t('tickets:validation.messageHintRejected')}
              {' '}{t('tickets:validation.messageHintHandoffs')}
            </>
          )}
        </p>
        {rejectNeedsRemark && (
          <p className="text-[11px] text-rose-600 mt-1">{t('tickets:validation.requiredRemarkRejected')}</p>
        )}
        {/* Optional reviewer attachment — uploaded onto the Uniticket ticket right after whichever of the
            three actions below succeeds (Save / Hand over / Send to Build). It used to follow Save ONLY,
            which silently dropped the file on the other two (user bug 2026-08-04, UT-2026-000506). */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300',
              (saving || lockedByOther) && 'pointer-events-none opacity-50',
            )}
          >
            <Paperclip className="h-3.5 w-3.5" />
            {t('tickets:validation.attachments.attachBtn')}
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = ''; // allow re-picking the same file after a clear
                if (!f) return;
                if (f.size > 10 * 1024 * 1024) {
                  toast.error(t('tickets:validation.attachments.tooBig'));
                  return;
                }
                setAttachFile(f);
              }}
            />
          </label>
          {attachFile ? (
            <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
              <span className="max-w-[16rem] truncate" title={attachFile.name}>{attachFile.name}</span>
              <span className="shrink-0 text-slate-400">
                {attachFile.size < 1024 * 1024
                  ? `${Math.max(1, Math.round(attachFile.size / 1024))} KB`
                  : `${(attachFile.size / 1024 / 1024).toFixed(1)} MB`}
              </span>
              <button
                type="button"
                onClick={() => setAttachFile(null)}
                aria-label={t('tickets:validation.attachments.remove')}
                className="shrink-0 rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <span className="text-[11px] text-slate-400">
              {t(multi ? 'tickets:validation.multi.attachHint' : 'tickets:validation.attachments.attachHint')}
            </span>
          )}
        </div>
        {/* Why the last attempt was refused — persistent, right where the action was triggered
            (user ask 2026-08-04). Disappears as soon as the reviewer changes anything. */}
        {/* Release reminder (user ask 2026-08-06): closing a ticket on an address nobody released leaves
            it invisible to the partner. Warning only — never blocks a verdict (an address can legitimately
            stay unreleased, e.g. a rejection). Shown while a final verdict is selected. */}
        {referenceAttributes && !sduReleased && !panels.noAddress
          && (form.status === 'VALIDATED' || form.status === 'REJECTED') && (
          <div
            role="note"
            className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t('tickets:validation.notReleasedVerdictWarning')}</span>
          </div>
        )}
        {saveBlocked && (
          <div
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-800"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              <strong className="font-semibold">{t('tickets:validation.saveBlockedTitle')}</strong>{' '}
              {saveBlocked}
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Button
            onClick={handleSave}
            disabled={saving || escalating || sendingToBuild || lockedByOther || form.status === 'ESCALATED' || form.status === 'WAITING_BUILD' || rejectNeedsRemark || houseExtIncoherent || mappingHouseNumNotDigits
              || (waitingFor != null && (form.status === 'VALIDATED' || form.status === 'REJECTED'))}
            className="bg-brand-gradient text-white hover:opacity-90"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {saving ? t('tickets:validation.saving') : t('tickets:validation.save')}
          </Button>
          {/* N45 — sur un ticket à plusieurs adresses, ces trois envois se font pour le ticket entier : ils vivent
              dans la carte « Le ticket entier », plus bas. */}
          {!multi && (<>
          <Button
            type="button"
            variant="outline"
            onClick={handleEscalate}
            disabled={saving || escalating || sendingToBuild || lockedByOther || !panels.escalationTarget
              || (form.status === 'ESCALATED' && panels.validation.canEscalate === false)}
            title={panels.escalationTarget ? undefined : t('tickets:validation.escalateNoTarget')}
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
          >
            {escalating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowUpRight className="w-4 h-4 mr-1.5" />}
            {panels.escalationTarget
              ? t('tickets:validation.escalate', { label: panels.escalationTarget.label })
              : t('tickets:validation.escalateNoTargetBtn')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSendToBuild}
            // `form.status === 'WAITING_BUILD'` NE désactive plus (défaut UT-2026-000450, 14-09) : une
            // ligne déjà parquée est précisément celle qu'on veut relancer. Le bouton grisé était la
            // moitié visible d'une garde serveur qui, elle, avalait le message sans rien dire.
            disabled={saving || escalating || sendingToBuild || lockedByOther || !panels.buildTarget}
            title={panels.buildTarget ? undefined : t('tickets:validation.sendToBuildNoTarget')}
            className="border-sky-300 text-sky-700 hover:bg-sky-50"
          >
            {sendingToBuild ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <HardHat className="w-4 h-4 mr-1.5" />}
            {/* Le libellé dit lequel des deux gestes on s'apprête à faire — « Envoyer à Build » sur une
                ligne déjà partie se lirait comme un doublon, et c'est ce doute qui a fait renvoyer le
                message plusieurs fois. */}
            {form.status === 'WAITING_BUILD'
              ? t('tickets:validation.sendToBuildAgain')
              : t('tickets:validation.sendToBuild')}
          </Button>
          {/* « Renvoyer » — only for the recipient of a live escalation (F4). The server computes
              returnTarget from the recorded escalator, so the button simply cannot appear for anyone
              else, and the endpoint re-checks it anyway. */}
          {panels.returnTarget && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen('return')}
              disabled={saving || escalating || sendingToBuild || returning || lockedByOther}
              className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
            >
              {returning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Reply className="w-4 h-4 mr-1.5" />}
              {t('tickets:validation.returnBtn', { label: panels.returnTarget.label })}
            </Button>
          )}
          </>)}
        </div>
        {/* Hand-off target hints (escalate + Build). */}
        {!multi && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
          <span className={cn('text-xs', panels.escalationTarget ? 'text-slate-500' : 'text-slate-400')}>
            {panels.escalationTarget
              ? t('tickets:validation.escalatesTo', { label: panels.escalationTarget.label })
              : t('tickets:validation.escalateNoTarget')}
          </span>
          <span className={cn('text-xs', panels.buildTarget ? 'text-slate-500' : 'text-slate-400')}>
            {panels.buildTarget
              ? t('tickets:validation.sendsToBuild', { label: panels.buildTarget.label })
              : t('tickets:validation.sendToBuildNoTarget')}
          </span>
          {/* N29.1 — « Build a répondu » : durable, c'est lui qui ouvre la question PST bis à la création. */}
          {id && (
            <BuildRepliedMarker
              ticketId={id}
              buildRepliedAt={panels.ticket.buildRepliedAt}
              buildRepliedSource={panels.ticket.buildRepliedSource}
              canMark={isAdmin}
              disabled={lockedByOther}
              onMarked={markBuildRepliedLocally}
            />
          )}
        </div>
        )}
        {/* Gated closure note */}
        <div className="flex items-start gap-2 mt-3 text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3">
          <Lock className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            <strong className="text-slate-600">{t('tickets:validation.gatedTitle')}</strong> —{' '}
            {t(multi ? 'tickets:validation.multi.gatedDesc' : 'tickets:validation.gatedDesc')}
          </span>
        </div>
      </GlassCard>
      )}

      {/* N45 — LE TICKET ENTIER (décisions ③ et ④ du 20-10) : la question à l'opérateur, l'envoi chez Build et
          l'escalade partent pour toutes ses adresses ; puis « Résoudre le ticket », quand chacune a son verdict. */}
      {multi && id && (
        <GlassCard className="p-4">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-brand-rose" />
            <h2 className="text-sm font-bold text-slate-800">{t('tickets:validation.multi.ticketCardTitle')}</h2>
          </div>
          <p className="text-xs text-slate-400">{t('tickets:validation.multi.ticketCardSub', { count: panels.addresses?.length ?? 0 })}</p>
          {renderTicketContact()}
          <div className="mt-4">
            <TicketResolveCard
              ticketId={id}
              resolvedAt={ticket.resolvedAt}
              refreshKey={JSON.stringify([
                panels.addresses?.map((a) => [a.jvid, a.status, a.ownedByOtherTicket]),
                panels.activeJvid, panels.validation.status, panels.validation.remark,
                ticket.buildHandedOffAt, ticket.buildReplyAt, ticket.questionAskedAt, ticket.operatorReplyAt,
                ticket.escalatedAt, ticket.escalatedToId, ticket.returnedAt, ticket.resolvedAt,
              ])}
              disabled={lockedByOther}
              onSelect={selectTab}
              onResolved={(res) => {
                // UN commentaire (le récapitulatif) puis RESOLVED : le ticket est fini, on passe au suivant comme
                // après le verdict d'un ticket à une adresse (N43 : le passage se voit, on peut revenir).
                const done = t('tickets:validation.multi.resolve.done');
                const doneToast = toast.success(done);
                const sync = res.uniticketSync;
                if (sync?.enabled === false) toast.message(t('tickets:validation.syncDisabled'));
                else if (sync?.resolved) toast.success(t('tickets:validation.multi.resolve.syncResolved'));
                if (sync?.error) toast.error(t('tickets:validation.syncFailed', { error: sync.error }), { duration: 8000 });
                setThreadVersion((v) => v + 1);
                void loadHistory();
                if (!nextTicketId) showPanels(res.data);
                advanceAfter(done, doneToast, sync?.reopen);
              }}
            />
          </div>
        </GlassCard>
      )}

      {/* Validation history (append-only audit trail) — this ticket's address first, the rest of
          the building collapsed + per-line ticket number (user ask 2026-07-30, case 691/793). */}
      <ValidationHistory
        history={history}
        currentJvid={panels.activeJvid ?? reference?.jvid ?? mapping?.jvid ?? null}
        currentTicketNumber={ticket.ticketNumber ?? null}
        currentTicketId={id ?? null}
      />
      </div>

      {/* MDU sibling — full address reference (reuses the Panel-1 view) */}
      {/* Le détail de capacité — UNE modale pour toute la page, pilotée par l'état, comme le tiroir
          d'appartement juste en dessous. Elle recalcule sur les unités du groupe : même entrée, même
          fonction, donc jamais un chiffre différent de celui de la pastille. */}
      {capacityGroup && (
        <CapacityDetailModal
          apartments={capacityGroup.rows}
          addressLabel={capacityGroup.label}
          cocon={facadeCocon}
          onClose={() => setCapacityGroup(null)}
        />
      )}
      {ssvTsaOpen && (
        <DetailModal
          title={`${t('tickets:dmpLookup.ssvTsa.title')} — ${ssvTsaOpen.label}`}
          onClose={() => setSsvTsaOpen(null)}
        >
          <SsvTsaCard docs={ssvTsaOpen.docs} />
        </DetailModal>
      )}
      {buildFileOpen && (
        <DetailModal
          title={`${t(`tickets:dmpLookup.buildFile.${buildFileOpen.kind}`)} — ${buildFileOpen.label}`}
          onClose={() => setBuildFileOpen(null)}
        >
          <BuildFileUnitsDetail
            title={`${t(`tickets:dmpLookup.buildFile.${buildFileOpen.kind}`)} — ${buildFileOpen.label}`}
            hits={buildFileOpen.hits}
          />
        </DetailModal>
      )}

      <Sheet open={selectedSibling != null} onOpenChange={(o) => { if (!o) { setSelectedSibling(null); setShowSiblingComment(false); } }}>
        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto p-5">
          <div className="flex items-center gap-2 pr-8">
            <SheetTitle className="text-base font-bold gradient-text">
              {t(
                selectedSibling?.isCurrent && !mduApartments?.length
                  ? 'tickets:validation.currentDetailTitle'   // SDU path: this IS the ticket's address
                  : 'tickets:validation.siblingDetailTitle',
                { jvid: selectedSibling?.jvid ?? '' },
              )}
            </SheetTitle>
            {/* Pop the operator's ticket comment into a left-hand volet — the page's "Ticket description"
                card is hidden behind this open drawer, so the reviewer would otherwise lose sight of what
                to correct while editing the mapping. */}
            <button
              type="button"
              onClick={() => setShowSiblingComment((v) => !v)}
              title={t('tickets:validation.siblingCommentToggle')}
              aria-label={t('tickets:validation.siblingCommentToggle')}
              aria-pressed={showSiblingComment}
              className={cn(
                'inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-colors shrink-0',
                showSiblingComment
                  ? 'bg-brand-rose/10 border-brand-rose/30 text-brand-rose'
                  : 'border-slate-200 text-slate-500 hover:text-brand-rose hover:border-brand-rose/30',
              )}
            >
              <MessageSquareText className="w-4 h-4" />
            </button>
          </div>

          {/* Ticket-comment volet — fills the empty space to the LEFT of the drawer (right inset is keyed to
              the sheet's max-w-4xl = 56rem), the height hugs the message and only scrolls once it would pass the
              viewport, and it slides in from the top. Non-modal so the right-side mapping form stays editable. */}
          {showSiblingComment && (
            <div className="fixed left-3 top-3 right-[56.75rem] z-50 flex max-h-[calc(100vh_-_1.5rem)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl animate-in fade-in-0 slide-in-from-top-8 duration-300">
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
                <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                  <MessageSquareText className="w-4 h-4 text-brand-rose" />
                  {t('tickets:validation.description')}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowSiblingComment(false)}
                  aria-label={t('tickets:validation.siblingCommentToggle')}
                  className="text-slate-400 transition-colors hover:text-slate-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="min-h-0 overflow-y-auto px-5 py-4">
                <p className="mb-2 text-xs text-slate-400">{ticket.ticketNumber} · {ticket.operatorName}</p>
                {ticket.subject?.trim() && (
                  <p className="mb-1 text-sm font-semibold text-slate-800">{ticket.subject}</p>
                )}
                <p className="whitespace-pre-line text-sm text-slate-600">
                  {ticket.description?.trim() || <span className="text-slate-400">{t('tickets:validation.noDescription')}</span>}
                </p>
              </div>
            </div>
          )}
          {selectedSibling && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  {t('tickets:validation.release.refHeading')}
                </p>
                <AttentionDetails ruleIds={computeAttentionPoints(selectedSibling.attributes)} />
                <AddressReferenceView
                  reference={selectedSibling.reference}
                  attributes={selectedSibling.attributes}
                  // Ticket tenu par un autre : pas de bouton, comme le release à côté (le serveur répondrait 423).
                  renderFix={sheetIsMdu && !lockedByOther ? renderSheetNetworkFix : undefined}
                />
              </div>
              <div className="md:border-l md:border-slate-100 md:pl-5">
                <ReleaseAddressPanel
                  key={selectedSibling.jvid}
                  sibling={selectedSibling}
                  siblings={mduApartments ?? []}
                  // N41 ⑤ — pour la seule recherche de doublons de déclaration (N38) : pas des « siblings ».
                  duplicateCandidates={strangers}
                  ticketId={id ?? ''}
                  onReleased={refreshSelectedSibling}
                  onClose={() => setSelectedSibling(null)}
                  // SDU current-address path: its mapping row is the very one Panel 2 edits — hide the
                  // sheet's mapping form so there is a single editor for that (jvid, operator) row.
                  hideMappingForm={selectedSibling.isCurrent && !mduApartments?.length}
                  // Anti-collision: another user works this ticket → the sheet stays readable but its
                  // release/unrelease/mapping actions are off (server also 423s them).
                  actionsDisabled={lockedByOther}
                />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* N28 — changer l'adresse d'un ticket en cours n'est pas un geste anodin : l'ancien et le nouveau
          JVID sont montrés côte à côte avant d'écrire, et la ligne part au journal d'actions. */}
      <ConfirmDialog
        open={relinkConfirm}
        tone="amber"
        icon={<AlertTriangle className="w-5 h-5" />}
        title={t('tickets:validation.relink.confirmTitle')}
        message={t('tickets:validation.relink.confirmBody', { from: relinkFrom ?? '—', to: linkJvid || '—' })}
        confirmLabel={t('tickets:validation.relink.confirmBtn')}
        cancelLabel={t('tickets:validation.relink.cancel')}
        busy={linking}
        onCancel={() => setRelinkConfirm(false)}
        onConfirm={() => { setRelinkConfirm(false); void handleLinkAddress(); }}
      />
      {/* 3.2 — Pending is not a final verdict: warn (soft) before saving as Pending. */}
      <ConfirmDialog
        open={pendingGuardOpen}
        tone="amber"
        icon={<AlertTriangle className="w-5 h-5" />}
        title={t('tickets:validation.pendingGuard.title')}
        message={t('tickets:validation.pendingGuard.body')}
        confirmLabel={t('tickets:validation.pendingGuard.saveAnyway')}
        cancelLabel={t('tickets:validation.pendingGuard.cancel')}
        busy={saving}
        onCancel={() => setPendingGuardOpen(false)}
        onConfirm={() => { setPendingGuardOpen(false); performSave(); }}
      />
      {/* Reject guard — see the long comment at the call site in handleSave for the why. */}
      <ConfirmDialog
        open={rejectGuardOpen}
        tone="rose"
        icon={<Ban className="w-5 h-5" />}
        title={t('tickets:validation.rejectGuard.title')}
        message={t('tickets:validation.rejectGuard.body')}
        confirmLabel={t('tickets:validation.rejectGuard.rejectAnyway')}
        cancelLabel={t('tickets:validation.rejectGuard.cancel')}
        secondaryLabel={t('tickets:validation.rejectGuard.askInstead')}
        onSecondary={() => {
          setRejectGuardOpen(false);
          setForm((prev) => (prev ? { ...prev, status: 'PENDING' } : prev));
          performSave('PENDING');
        }}
        busy={saving}
        onCancel={() => setRejectGuardOpen(false)}
        onConfirm={() => { setRejectGuardOpen(false); performSave(); }}
      />
      {/* Styled confirms for the two hand-off actions (replace the native window.confirm dialogs). */}
      <ConfirmDialog
        open={confirmOpen === 'escalate'}
        tone="orange"
        icon={<ArrowUpRight className="w-5 h-5" />}
        title={panels.escalationTarget
          ? t('tickets:validation.escalate', { label: panels.escalationTarget.label })
          : t('tickets:validation.escalateNoTargetBtn')}
        message={panels.escalationTarget
          ? t('tickets:validation.escalateConfirm', { label: panels.escalationTarget.label })
          : ''}
        confirmLabel={t('tickets:validation.confirmProceed')}
        cancelLabel={t('tickets:validation.pendingGuard.cancel')}
        onCancel={() => setConfirmOpen(null)}
        onConfirm={() => { setConfirmOpen(null); void (ticketLevel ? doTicketEscalate() : doEscalate()); }}
      />
      <ConfirmDialog
        open={confirmOpen === 'return'}
        tone="orange"
        icon={<Reply className="w-5 h-5" />}
        title={panels.returnTarget ? t('tickets:validation.returnBtn', { label: panels.returnTarget.label }) : ''}
        message={panels.returnTarget ? t('tickets:validation.returnConfirm', { label: panels.returnTarget.label }) : ''}
        confirmLabel={t('tickets:validation.confirmProceed')}
        cancelLabel={t('tickets:validation.pendingGuard.cancel')}
        busy={returning}
        onCancel={() => setConfirmOpen(null)}
        onConfirm={() => {
          setConfirmOpen(null);
          void (ticketLevel ? doTicketReturnEscalation() : doReturnEscalation());
        }}
      />
      <ConfirmDialog
        open={confirmOpen === 'build'}
        tone="sky"
        icon={<HardHat className="w-5 h-5" />}
        title={t('tickets:validation.sendToBuild')}
        message={panels.buildTarget ? (
          <div className="space-y-3">
            <p>{t('tickets:validation.sendToBuildConfirm', { label: panels.buildTarget.label })}</p>
            {/* Optional PUBLIC operator question riding along (row-level only — the no-address mode
                has its own composer, and so does a multi-address ticket, N45). Posted as a separate public
                comment + opens the reply-watch. */}
            {!ticketLevel && (
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  {t('tickets:validation.buildQuestionLabel')}
                </label>
                <textarea
                  value={buildQuestion}
                  onChange={(e) => setBuildQuestion(e.target.value)}
                  rows={MESSAGE_ROWS}
                  placeholder={t('tickets:validation.buildQuestionPlaceholder')}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-rose/40"
                />
                <p className="mt-1 text-[10px] text-slate-400">{t('tickets:validation.buildQuestionHint')}</p>
              </div>
            )}
          </div>
        ) : ''}
        confirmLabel={t('tickets:validation.confirmProceed')}
        cancelLabel={t('tickets:validation.pendingGuard.cancel')}
        onCancel={() => setConfirmOpen(null)}
        onConfirm={() => { setConfirmOpen(null); void (ticketLevel ? doTicketSendToBuild() : doSendToBuild()); }}
      />
      {/* N45 ⓔ — quitter un onglet sur une correction, une note ou un verdict pas encore enregistrés. */}
      <ConfirmDialog
        open={pendingTab != null}
        tone="amber"
        icon={<AlertTriangle className="w-5 h-5" />}
        title={t('tickets:validation.multi.unsavedTitle')}
        message={t('tickets:validation.multi.unsavedBody', {
          label: activeAddress ? ticketAddressLabel(activeAddress) : '—',
        })}
        confirmLabel={t('tickets:validation.multi.unsavedLeave')}
        cancelLabel={t('tickets:validation.multi.unsavedStay')}
        onCancel={() => setPendingTab(null)}
        onConfirm={() => { const jvid = pendingTab; setPendingTab(null); if (jvid != null) openTab(jvid); }}
      />
    </div>
  );
}

// ── Small presentational helpers ──────────────────────────────

function ContextItem({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="block text-[10px] uppercase tracking-wide text-slate-400">{k}</span>
      {children}
    </div>
  );
}

function ValidationPill({ status }: { status: AddressValidationStatus }) {
  const styles: Record<AddressValidationStatus, string> = {
    PENDING: 'bg-amber-50 text-amber-700',
    VALIDATED: 'bg-emerald-50 text-emerald-700',
    REJECTED: 'bg-rose-50 text-rose-700',
    ESCALATED: 'bg-orange-50 text-orange-700',
    WAITING_BUILD: 'bg-sky-50 text-sky-700',
  };
  return (
    <span className={cn('text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide', styles[status])}>
      {status.toLowerCase().replace(/_/g, ' ')}
    </span>
  );
}

function statusActive(s: AddressValidationStatus): string {
  if (s === 'VALIDATED') return 'bg-emerald-50 text-emerald-700 border-emerald-300';
  if (s === 'REJECTED') return 'bg-rose-50 text-rose-700 border-rose-300';
  return 'bg-amber-50 text-amber-700 border-amber-300';
}

function Banner({ tone, icon, children }: { tone: 'warn' | 'info'; icon: React.ReactNode; children: React.ReactNode }) {
  const styles = tone === 'warn'
    ? 'bg-amber-50 border-amber-200 text-amber-800'
    : 'bg-sky-50 border-sky-200 text-sky-800';
  return (
    <div className={cn('flex items-start gap-2 rounded-xl border px-4 py-2.5 text-sm', styles)}>
      <span className="shrink-0 mt-0.5">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

// Append-only validation audit trail for this address (who validated/corrected what, when).
interface AuditDetails {
  status?: string;
  remark?: string | null;
  jvid?: number;
  edits?: Record<string, { from: unknown; to: unknown }>;
  /** UT-… the action came through (stamped by the validation audits; absent on older rows). */
  ticketNumber?: string;
  /** The ticket's id, resolved from that number at read time — makes the entry a link (F7). */
  ticketId?: string;
  /** Escalation audits: WHO the hand-off targeted (user ask 2026-08-03 — readable history). */
  target?: { userId?: string; label?: string | null } | null;
  /** ADDRESS_BOXES_ADD : le passage outre la capacité, AVEC le calcul tel qu'il a été montré.
   *  Écrit depuis le 04-09, **affiché depuis le 10-09 seulement** — il a donc vécu en écriture seule,
   *  lisible par SQL et par personne d'autre. Optionnel à dessein : les lignes d'avant ne l'ont pas,
   *  et une ligne d'audit ne se réécrit jamais (table append-only). */
  capacityOverride?: {
    reason?: string;
    verdict?: string;
    available?: number | null;
    requested?: number;
    quadrant?: string | null;
    rule?: string;
    /** Depuis C0.1 (17-09) : la position QDX déclarée, et le boîtier façade tel que COCON le montrait. */
    unitPosition?: 'FACADE' | 'REAR' | null;
    cocon?: { boxes?: number; connected?: number; planned?: number; available?: number } | null;
  } | null;
  /** ADDRESS_BOXES_ADD, depuis C0.1 : la position QDX déclarée, tracée même sans passage outre. */
  unitPosition?: 'FACADE' | 'REAR' | null;
  /** N31 — TICKET_UNLINK_ADDRESS : le motif, obligatoire, de la déliaison. */
  reason?: string;
  /** N28 — TICKET_LINK_ADDRESS : le JVID que la liaison a REMPLACÉ (null pour une première liaison). */
  previousJvid?: number | null;
  /** TICKET_QUESTION_ASKED : une note interne plutôt qu'une question à l'opérateur. */
  internal?: boolean;
  /** TICKET_ATTACHMENT_UPLOADED : le fichier déposé. */
  fileName?: string;
  /** TICKET_BUILD_REPLIED_MANUAL : le mot, obligatoire, de qui a marqué la réponse de Build. */
  note?: string;
}

// The five VALIDATION statuses, as a runtime list. `details.status` is a free-form field of an audit
// payload, not a promise: a creation audit carries the DMP ADDRESS status there ('HOMES_PASSED'), and
// feeding that to ValidationPill produced an unstyled « homes passed » chip. Anything not in this list
// is not a verdict and must not be dressed as one.
const VALIDATION_STATUSES: AddressValidationStatus[] = [
  'PENDING', 'VALIDATED', 'REJECTED', 'ESCALATED', 'WAITING_BUILD',
];
const isValidationStatus = (s: unknown): s is AddressValidationStatus =>
  typeof s === 'string' && (VALIDATION_STATUSES as string[]).includes(s);

// Non-verdict audit actions → an i18n label suffix under validation.history.action.*
const HISTORY_ACTION_KEY: Record<string, string> = {
  ADDRESS_MAPPING_UPSERT: 'mapping',
  ADDRESS_RELEASE: 'released',
  ADDRESS_UNRELEASE: 'unreleased',
  ADDRESS_CREATE: 'created',
  ADDRESS_BOXES_ADD: 'boxesAdded',
  ADDRESS_SDU_TO_MDU: 'convertedToMdu',
  // N31 (choix du user, 29-09) — écrite AUSSI sur la ligne du JVID, pour que le prochain ticket de cette adresse
  // voie qu'un ticket y a été lié par erreur, puis délié, et pourquoi.
  TICKET_UNLINK_ADDRESS: 'unlinked',
  // N31.1 (30-09) — les événements du TICKET lui-même, lus aussi quand il n'a pas de JVID : l'historique ne
  // disparaît plus quand une liaison est défaite. Deux gestes ont deux sens : voir historyActionKey.
  TICKET_LINK_ADDRESS: 'linked',
  TICKET_QUESTION_ASKED: 'questionAsked',
  TICKET_SENT_TO_BUILD: 'sentToBuild',
  TICKET_ESCALATED: 'escalated',
  TICKET_ESCALATION_RETURNED: 'escalationReturned',
  TICKET_RECLASSIFIED: 'reclassified',
  TICKET_ATTACHMENT_UPLOADED: 'attachmentUploaded',
  TICKET_BUILD_REPLIED_MANUAL: 'buildRepliedManual',
  // N45 — les gestes d'un ticket à plusieurs adresses.
  TICKET_ADDRESS_ADD: 'addressAdded',
  TICKET_ADDRESS_REMOVE: 'addressRemoved',
  TICKET_RESOLVE: 'ticketResolved',
  TICKET_RESOLUTION_REOPENED: 'resolutionReopened',
};

/** Le libellé d'une entrée : la table ci-dessus, affinée quand un même geste a deux sens — une liaison qui
 *  REMPLACE un JVID, une question qui n'est qu'une note interne. */
function historyActionKey(h: AuditLogItem, d: AuditDetails): string | undefined {
  if (h.action === 'TICKET_LINK_ADDRESS' && d.previousJvid != null) return 'relinked';
  if (h.action === 'TICKET_QUESTION_ASKED' && d.internal) return 'internalNote';
  return HISTORY_ACTION_KEY[h.action];
}

/** TICKET_RECLASSIFIED : la file visée. Lu à part : `target` désigne ailleurs le destinataire d'une escalade. */
function reclassifiedTarget(h: AuditLogItem): string | null {
  if (h.action !== 'TICKET_RECLASSIFIED') return null;
  const target = (h.details as Record<string, unknown> | null)?.target;
  return typeof target === 'string' ? target : null;
}

// The address (jvid) an audit entry applies to — from details.jvid, else parsed from the entityId
// ('jvid:operator' for address_validation, 'jvid' for address). Add-box is building-scoped → no jvid.
function jvidOfAudit(h: AuditLogItem, d: AuditDetails): number | null {
  if (typeof d.jvid === 'number') return d.jvid;
  if (h.action === 'ADDRESS_BOXES_ADD') return null;
  if (h.entityType === 'address_validation') {
    const n = Number((h.entityId ?? '').split(':')[0]);
    return Number.isFinite(n) ? n : null;
  }
  if (h.entityType === 'address') {
    const n = Number(h.entityId);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function ValidationHistory({ history, currentJvid, currentTicketNumber, currentTicketId }: {
  history: AuditLogItem[];
  currentJvid: number | null;
  currentTicketNumber: string | null;
  /** The ticket being reviewed — its own entries stay plain text (no link back to this same page). */
  currentTicketId: string | null;
}) {
  const { t } = useTranslation(['tickets']);
  // « Autres adresses du bâtiment » collapsed by default (user ask 2026-07-30): a verdict on a
  // SIBLING — possibly via another ticket — used to read as "this ticket was validated"
  // (case UT-2026-000691 vs 000793).
  const [showOthers, setShowOthers] = useState(false);
  if (!history.length) return null;

  // Split by ADDRESS: this ticket's jvid (and building-scoped entries with no jvid, e.g. add-box)
  // stay in the main section; every other apartment of the building goes to the collapsed one.
  // With no current jvid (no-address mode) everything stays in the main section.
  const isOwn = (h: AuditLogItem) => {
    // N31.1 : un événement du TICKET est le sien, quel que soit le JVID qu'il nomme — une déliaison nomme l'ANCIEN
    // JVID, elle n'a rien à faire dans « autres adresses du bâtiment ».
    if (h.entityType === 'ticket') return true;
    const jvid = jvidOfAudit(h, (h.details ?? {}) as AuditDetails);
    return currentJvid == null || jvid == null || jvid === currentJvid;
  };
  const own = history.filter(isOwn);
  const others = history.filter((h) => !isOwn(h));

  const renderEntry = (h: AuditLogItem) => {
    const d = (h.details ?? {}) as AuditDetails;
    const edits = d.edits ? Object.entries(d.edits) : [];
    const actionKey = historyActionKey(h, d);
    const reclassTarget = reclassifiedTarget(h);
    const jvid = jvidOfAudit(h, d);
    // Per-line ticket context: which ticket the action came through — with a warning badge when it
    // is NOT the ticket being reviewed (old audit rows may predate the ticketNumber detail).
    const ticketNumber = typeof d.ticketNumber === 'string' && d.ticketNumber ? d.ticketNumber : null;
    const otherTicket = ticketNumber != null && currentTicketNumber != null && ticketNumber !== currentTicketNumber;
    return (
      <li key={h.id} className="flex gap-3">
        <div className="shrink-0 mt-0.5">
          {/* A NAMED action wins over details.status — they are not the same vocabulary. ADDRESS_CREATE
              carries the DMP address status ('HOMES_PASSED') in its details, and this branch used to
              hand it to ValidationPill: an unstyled « homes passed » chip, while « Adresse créée »
              was never reached. That is Anouk's « dans la validation history, je trouve que c'est pas
              clair que j'ai créé » (2026-08-10, l. 1331) — the label existed, the code never got there. */}
          {actionKey ? (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                // The creation is the address's ORIGIN event — it gets the strong treatment so it stands
                // out of the run of mapping/release lines. Deliberately not a status colour: it is not a
                // verdict, and emerald/amber here would read as one.
                h.action === 'ADDRESS_CREATE' ? 'bg-slate-900 text-white'
                  // Une déliaison est une RÉPARATION : elle doit se voir dans une suite de lignes grises.
                  : h.action === 'TICKET_UNLINK_ADDRESS' ? 'bg-rose-100 text-rose-700'
                  : 'bg-slate-100 text-slate-500',
              )}
            >
              {t(`tickets:validation.history.action.${actionKey}`)}
            </span>
          ) : isValidationStatus(d.status) ? (
            <ValidationPill status={d.status} />
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
              {h.action}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-600">
            <span className="font-semibold text-slate-800">
              {h.userDisplayName || h.userEmail || t('tickets:validation.history.system')}
            </span>
            <span className="text-slate-400"> · {formatDate(h.createdAt)}</span>
            {jvid != null && (
              <span className="text-slate-400"> · JVID <JvidLink jvid={jvid} /></span>
            )}
            {/* Duplicate navigation (user ask 2026-08-06, F7): two operators frequently open the SAME
                request (000133 / 000224) and the reviewer needs to compare them before validating the
                second with « déjà traité sur le ticket X ». The number was plain text — now it's the
                way there, in both directions (each ticket's history names the other). */}
            {ticketNumber && (
              <span className="text-slate-400">
                {' · '}
                {d.ticketId && d.ticketId !== currentTicketId ? (
                  <Link
                    to={`/tickets/${d.ticketId}/address-validation`}
                    onClick={(e) => e.stopPropagation()}
                    title={t('tickets:validation.history.openTicket', { ticket: ticketNumber })}
                    className="font-semibold text-brand-rose underline decoration-brand-rose/40 underline-offset-2 hover:decoration-brand-rose"
                  >
                    {ticketNumber}
                  </Link>
                ) : ticketNumber}
              </span>
            )}
            {/* Escalation entries name their TARGET (user ask 2026-08-03): « → à traiter par X ». */}
            {h.action === 'ADDRESS_VALIDATION_ESCALATED' && d.target?.label && (
              <span className="ml-1.5 font-semibold text-orange-700">
                {t('tickets:validation.history.escalatedTo', { label: d.target.label })}
              </span>
            )}
            {otherTicket && (
              <span
                className="ml-1.5 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 align-middle"
                title={t('tickets:validation.history.otherTicketTitle', { ticket: ticketNumber })}
              >
                {t('tickets:validation.history.otherTicketBadge')}
              </span>
            )}
          </div>
          {d.remark && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{d.remark}</p>}
          {h.action === 'TICKET_UNLINK_ADDRESS' && d.reason && (
            <p className="text-xs text-rose-700 mt-0.5 whitespace-pre-line">
              {t('tickets:validation.history.unlinkedReason', { reason: d.reason })}
            </p>
          )}
          {h.action === 'TICKET_LINK_ADDRESS' && d.previousJvid != null && (
            <p className="text-xs text-slate-500 mt-0.5">
              {t('tickets:validation.history.relinkedFrom', { from: d.previousJvid, to: d.jvid ?? '—' })}
            </p>
          )}
          {reclassTarget && (
            <p className="text-xs text-slate-500 mt-0.5">
              {t(`tickets:validation.history.reclassifiedTo.${reclassTarget}`, { defaultValue: reclassTarget })}
            </p>
          )}
          {h.action === 'TICKET_ATTACHMENT_UPLOADED' && d.fileName && (
            <p className="text-xs text-slate-500 mt-0.5 break-all">{d.fileName}</p>
          )}
          {h.action === 'TICKET_BUILD_REPLIED_MANUAL' && d.note && (
            <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{d.note}</p>
          )}
          {/* Passage outre la capacité : le MOTIF, et le calcul qui était sous les yeux au moment du
              geste. Sans le calcul, la trace dirait « il a forcé » sans dire ce qu'il voyait — c'est
              l'intention d'origine de `capacityOverride`, restée lettre morte tant que rien ne
              l'affichait. Rendu conditionnel champ par champ : les lignes d'audit anciennes n'ont pas
              toujours l'objet, et une ligne d'audit ne se corrige pas. */}
          {d.capacityOverride?.reason && (
            <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1">
              <p className="text-[11px] font-semibold text-amber-800">
                {t('tickets:validation.history.capacityOverride')}
              </p>
              <p className="text-xs text-amber-900 whitespace-pre-line">{d.capacityOverride.reason}</p>
              <p className="text-[10px] text-amber-700 mt-0.5">
                {t('tickets:validation.history.capacityOverrideCalc', {
                  requested: d.capacityOverride.requested ?? '—',
                  available: d.capacityOverride.available ?? '—',
                  quadrant: d.capacityOverride.quadrant || '—',
                  // La règle en clair. Les lignes écrites avant C0.1 portent `pole4` / `facadeBox8` : leurs
                  // libellés restent au dictionnaire, et une règle inconnue s'affiche telle quelle.
                  rule: d.capacityOverride.rule
                    ? t(`tickets:validation.capacity.rules.${d.capacityOverride.rule}`, { defaultValue: d.capacityOverride.rule })
                    : '—',
                })}
              </p>
              {d.capacityOverride.cocon && (
                <p className="text-[10px] text-amber-700">
                  {t('tickets:validation.history.capacityOverrideCocon', {
                    boxes: d.capacityOverride.cocon.boxes ?? '—',
                    connected: d.capacityOverride.cocon.connected ?? '—',
                    planned: d.capacityOverride.cocon.planned ?? '—',
                    available: d.capacityOverride.cocon.available ?? '—',
                  })}
                </p>
              )}
            </div>
          )}
          {/* La position QDX déclarée (C0.1) : l'app ne peut pas la vérifier, la trace est tout ce qui reste. */}
          {(d.capacityOverride?.unitPosition ?? d.unitPosition) && (
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t('tickets:validation.history.unitPosition', {
                position: t(`tickets:validation.history.position${d.capacityOverride?.unitPosition ?? d.unitPosition}`),
              })}
            </p>
          )}
          {edits.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {edits.map(([field, ft]) => (
                <li key={field} className="text-[11px] text-slate-500">
                  <span className="font-medium text-slate-600">{field}</span>:{' '}
                  <span className="line-through text-slate-400">{String(ft.from ?? '') || '—'}</span>
                  {' → '}
                  <span className="text-emerald-700">{String(ft.to ?? '') || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </li>
    );
  };

  return (
    <GlassCard className="p-4">
      <h2 className="text-sm font-bold text-slate-800 mb-3">{t('tickets:validation.history.title')}</h2>
      {own.length > 0 ? (
        <ol className="space-y-3">{own.map(renderEntry)}</ol>
      ) : (
        <p className="text-xs text-slate-400">{t('tickets:validation.history.noOwnEntries')}</p>
      )}
      {others.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => setShowOthers((v) => !v)}
            aria-expanded={showOthers}
            className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
          >
            <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', showOthers && 'rotate-90')} />
            {t('tickets:validation.history.othersToggle', { count: others.length })}
          </button>
          {showOthers && <ol className="space-y-3 mt-3">{others.map(renderEntry)}</ol>}
        </div>
      )}
    </GlassCard>
  );
}
